import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as Yup from "yup";
import posthog from "posthog-js";
import imageCompression from "browser-image-compression";
import Select from "react-select";
import { Dimmer, Segment, Container } from "semantic-ui-react";
import { Row, Col } from "react-bootstrap";
import { useSubscriptionStatus } from "../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../common/subscriptionRestriction/SubscriptionRestriction";

import {
  onRegisterByAdmin,
  onEditUser,
  isLoading,
  onGetUser,
  onGetAdminRoles,
  onGetOffices,
  onGetApprovers,
  onGetDepartments,
  onToggleUserStatus,
  onUpdateProfileImage,
  users,
  onGetAccrualSettings,
  onSaveAccrualSettings,
  onDeleteUser,
} from "./userActions";
import { onGetLeaveTypes } from "../../../components/common/header/settings/leaveTypes/leaveActions";
import * as designationActions from "../designation/designationActions";
import * as employmentTypeActions from "../employmentType/employmentTypeActions";
import * as teamActions from "../team/teamActions";
import { incrementOfficeUserCount } from "../company/companyOffices/officeActions";
import { messages } from "../../../utils";
import { getPreSignedUrl, uploadImgToS3 } from "../../../utils/common";
import { Common } from "../../../utils/common";
import { ACCRUAL_START_DATE_OPTIONS, ACCRUAL_START_DATE_VALUES, ACCRUAL_FREQUENCY_OPTIONS } from "../../../utils/constants";
import FetchingSpinner from "../../common/FetchingSpinner";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { CountryDropdown } from "../../common";
import { onGetCountries } from "../company/companyOffices/officeActions";
import { CUSTOM_ENROLL, fetchEnrollOptions } from "../attendance/attendanceMachineApi";
import "./userForm.css";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";
import {
  EMPTY_ADDITIONAL_PROFILE,
  EMPTY_DEPENDENT,
  resolveTeamIdsFromUser,
  resolveAdditionalProfileFromUser,
  buildAdditionalProfilePayload,
  buildTeamIdsPayload,
  getUserFormGenderOptions,
  getLocalDateInputMax,
} from "./userFormHelpers";
import { MeritalStatusOptions, BloodOptions } from "../../Global/Constant";
import { createAdditionalProfileValidationSchema } from "./userFormAdditionalProfileValidation";
import { createUserFormValidationSchema } from "./userFormValidationRules";
import UserFormValidationGuide from "./UserFormValidationGuide";

const { validation } = messages;

const USER_FORM_RS_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: "1rem",
    borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
};

function UserFormIconSelect({ icon, invalid, children, className = "" }) {
  return (
    <div className={`user-form-icon-select ${invalid ? "is-invalid" : ""} ${className}`.trim()}>
      <span className="user-form-icon-select__icon" aria-hidden="true">
        <i className={`bi ${icon}`} />
      </span>
      <div className="user-form-icon-select__control">{children}</div>
    </div>
  );
}

function UserFormTextField({
  id,
  label,
  icon,
  optional = false,
  required = false,
  value,
  onChange,
  onBlur,
  error,
  touched,
  type = "text",
  disabled = false,
  as = "input",
  rows = 3,
  colClass = "col-12 col-md-6",
  min,
  max,
  maxLength,
  placeholder,
  hint,
}) {
  const invalid = error && touched;
  const Control = as;
  return (
    <div className={colClass}>
      <div className="d-flex align-items-center justify-content-between mb-2 flex-nowrap gap-2">
        <label htmlFor={id} className="form-label fw-semibold mb-0 text-nowrap">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
          {optional && (
            <span className="text-muted fw-normal ms-1 small">(Optional)</span>
          )}
        </label>
        {invalid && (
          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
            {error}
          </small>
        )}
      </div>
      <div className="input-group d-flex align-items-stretch">
        <span
          className="input-group-text bg-light d-flex align-items-center justify-content-center"
          style={{
            fontSize: "0.875rem",
            padding: 0,
            minWidth: "auto",
            width: "40px",
            borderRight: "none",
          }}
        >
          <i className={`bi ${icon}`} />
        </span>
        <Control
          type={as === "input" ? type : undefined}
          rows={as === "textarea" ? rows : undefined}
          className={`form-control flex-grow-1 ${invalid ? "is-invalid" : ""}`}
          style={
            as === "textarea"
              ? { minHeight: "38px", padding: "0.375rem 0.75rem" }
              : { padding: "0.375rem 0.75rem", minHeight: "38px" }
          }
          id={id}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          min={as === "input" && type === "date" ? min : undefined}
          max={as === "input" && type === "date" ? max : undefined}
          aria-describedby={hint ? `${id}-hint` : undefined}
        />
      </div>
      {hint && (
        <small id={`${id}-hint`} className="form-text text-muted d-block mt-2">
          {hint}
        </small>
      )}
    </div>
  );
}

const enrollOptionsHas = (options, value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  return (options || []).some((e) => e.localeCompare(v, undefined, { sensitivity: "accent" }) === 0);
};

function UserFormEnrollField({
  id,
  label,
  icon,
  optional = false,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled = false,
  hint,
  placeholder,
  maxLength = 20,
  enrollOptions = [],
}) {
  const { t } = useTranslation();
  const [wantCustom, setWantCustom] = useState(false);
  const invalid = error && touched;
  const current = String(value || "").trim();
  const displayOptions = useMemo(() => {
    const list = [...(enrollOptions || [])];
    if (current && !wantCustom && !enrollOptionsHas(list, current)) {
      return [current, ...list];
    }
    return list;
  }, [enrollOptions, current, wantCustom]);
  const inList = enrollOptionsHas(displayOptions, current);

  useEffect(() => {
    if (current && !inList) setWantCustom(true);
    else if (inList) setWantCustom(false);
  }, [current, inList]);

  const selectValue = wantCustom || (current && !inList) ? CUSTOM_ENROLL : current;
  const showCustom = selectValue === CUSTOM_ENROLL;

  const emit = (next) => {
    const trimmed = String(next || "").trim();
    onChange({
      target: { value: trimmed.slice(0, maxLength) },
    });
  };

  return (
    <div className="col-12 col-md-6">
      <div className="d-flex align-items-center justify-content-between mb-2 flex-nowrap gap-2">
        <label htmlFor={id} className="form-label fw-semibold mb-0 text-nowrap">
          {label}
          {optional && (
            <span className="text-muted fw-normal ms-1 small">(Optional)</span>
          )}
        </label>
        {invalid && (
          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
            {error}
          </small>
        )}
      </div>
      <div className="input-group d-flex align-items-stretch">
        <span
          className="input-group-text bg-light d-flex align-items-center justify-content-center"
          style={{
            fontSize: "0.875rem",
            padding: 0,
            minWidth: "auto",
            width: "40px",
            borderRight: "none",
          }}
        >
          <i className={`bi ${icon}`} />
        </span>
        <select
          className={`form-select flex-grow-1 ${invalid ? "is-invalid" : ""}`}
          style={{ padding: "0.375rem 0.75rem", minHeight: "38px" }}
          id={id}
          value={selectValue}
          onChange={(e) => {
            const next = e.target.value;
            if (next === CUSTOM_ENROLL) {
              setWantCustom(true);
              if (inList) emit("");
              return;
            }
            setWantCustom(false);
            emit(next);
          }}
          onBlur={onBlur}
          disabled={disabled}
          aria-describedby={hint ? `${id}-hint` : undefined}
        >
          <option value="">{placeholder || t("app.attendance.machines.enrollSelectPlaceholder")}</option>
          {(displayOptions || []).map((enroll) => (
            <option key={enroll} value={enroll}>
              {enroll}
            </option>
          ))}
          <option value={CUSTOM_ENROLL}>{t("app.attendance.machines.enrollCustomOption")}</option>
        </select>
      </div>
      {showCustom ? (
        <input
          className={`form-control user-form-enroll-custom ${invalid ? "is-invalid" : ""}`}
          value={current}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={t("app.users.enterEmployeeCode")}
          onChange={(e) => emit(e.target.value)}
          onBlur={onBlur}
        />
      ) : null}
      {hint && (
        <small id={`${id}-hint`} className="form-text text-muted d-block mt-2">
          {hint}
        </small>
      )}
    </div>
  );
}

function UserFormNativeSelectField({
  id,
  label,
  icon,
  optional = false,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled = false,
  children,
  placeholder = "Select",
}) {
  const invalid = error && touched;
  return (
    <div className="col-12 col-md-6">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <label htmlFor={id} className="form-label fw-semibold mb-0">
          {label}
          {optional && (
            <span className="text-muted fw-normal ms-1 small">(Optional)</span>
          )}
        </label>
        {invalid && (
          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
            {error}
          </small>
        )}
      </div>
      <div className="input-group d-flex align-items-stretch">
        <span
          className="input-group-text bg-light d-flex align-items-center justify-content-center"
          style={{
            fontSize: "0.875rem",
            padding: 0,
            minWidth: "auto",
            width: "40px",
            borderRight: "none",
          }}
        >
          <i className={`bi ${icon}`} />
        </span>
        <select
          className={`form-select flex-grow-1 ${invalid ? "is-invalid" : ""}`}
          style={{ padding: "0.375rem 0.75rem", minHeight: "38px" }}
          id={id}
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
        >
          <option value="">{placeholder}</option>
          {children}
        </select>
      </div>
    </div>
  );
}

function UserFormCountryField({
  id,
  label,
  icon = "bi-globe-americas",
  optional = false,
  value,
  onChange,
  onBlur,
  error,
  touched,
  countries = [],
  loading = false,
  placeholder = "Select Country",
}) {
  const invalid = error && touched;
  return (
    <div className="col-12 col-md-6">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <label htmlFor={id} className="form-label fw-semibold mb-0">
          {label}
          {optional && (
            <span className="text-muted fw-normal ms-1 small">(Optional)</span>
          )}
        </label>
        {invalid && (
          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
            {error}
          </small>
        )}
      </div>
      <div
        id={id}
        className={`user-form-icon-select user-form-country-field ${invalid ? "is-invalid" : ""}`}
      >
        <span className="user-form-icon-select__icon" aria-hidden="true">
          <i className={`bi ${icon}`} />
        </span>
        <div className="user-form-icon-select__control">
          <CountryDropdown
            selection
            search
            clearable
            fluid
            scrolling
            name={id}
            value={value === "" || value == null ? "" : Number(value)}
            options={countries}
            placeholder={placeholder}
            className="user-form-country-dropdown w-100"
            onChange={(e, { value: selectedValue }) => onChange(selectedValue ?? "")}
            onBlur={onBlur}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}

function additionalProfileFieldProps(fieldId, errors, touched, setFieldTouched, submitCount = 0) {
  const fieldTouched = touched.additionalProfile?.[fieldId];
  return {
    error: errors.additionalProfile?.[fieldId],
    touched: fieldTouched || submitCount > 0,
    onBlur: () => setFieldTouched(`additionalProfile.${fieldId}`, true),
  };
}

const ADDITIONAL_PROFILE_SECTIONS = {
  personal: "personal",
  contact: "contact",
  address: "address",
  dependents: "dependents",
};

const DEFAULT_EXPANDED_ADDITIONAL_SECTIONS = {
  personal: true,
  contact: true,
  address: true,
  dependents: true,
};

function UserFormCollapsibleSubsection({ id, icon, title, expanded, onToggle, children, className = "" }) {
  const panelId = `user-form-subsection-${id}`;
  return (
    <div className={`user-form-collapsible-subsection ${className}`.trim()}>
      <button
        type="button"
        className={`user-form-subsection-title user-form-subsection-toggle${expanded ? " is-expanded" : ""}`}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <span className="user-form-subsection-toggle__label">
          <i className={`bi ${icon}`} aria-hidden="true" />
          {title}
        </span>
        <i
          className={`bi bi-chevron-${expanded ? "up" : "down"} user-form-subsection-toggle__chevron`}
          aria-hidden="true"
        />
      </button>
      <div
        id={panelId}
        className={`user-form-subsection-body${expanded ? " is-expanded" : " is-collapsed"}`}
        hidden={!expanded}
      >
        {children}
      </div>
    </div>
  );
}

const months = [
  { key: 1, text: "January", value: 1 },
  { key: 2, text: "February", value: 2 },
  { key: 3, text: "March", value: 3 },
  { key: 4, text: "April", value: 4 },
  { key: 5, text: "May", value: 5 },
  { key: 6, text: "June", value: 6 },
  { key: 7, text: "July", value: 7 },
  { key: 8, text: "August", value: 8 },
  { key: 9, text: "September", value: 9 },
  { key: 10, text: "October", value: 10 },
  { key: 11, text: "November", value: 11 },
  { key: 12, text: "December", value: 12 },
];

function UserLeaveSettingsInitializer({
  mode,
  allLeaveTypes,
  userSettings,
  initializedRef,
  setFieldValue,
  dispatch,
  userStore,
  customAccrualSettings,
  setCustomAccrualSettings,
  setCustomAccrualRateUnits,
  setCustomAccrualCapUnits,
}) {
  const accrualSettingsFetchedRef = React.useRef(false);
  const customSettingsProcessedRef = React.useRef(new Set());

  // Fetch custom accrual settings for all leave types
  React.useEffect(() => {
    if (
      mode === "edit" &&
      allLeaveTypes &&
      allLeaveTypes.length > 0 &&
      userStore.users?.id &&
      !accrualSettingsFetchedRef.current
    ) {
      accrualSettingsFetchedRef.current = true;
      const userId = userStore.users.id;

      // First, populate customAccrualSettings from userSettings if they have custom accrual
      // Exclude cases where frequency is "Annually" and enableAccrual is false (Front-loaded Annual Grant - use defaults)
      const settingsFromUserSettings = {};
      userSettings.forEach(us => {
        const hasCustomAccrual = us.isCustomAccrualLeave === true ||
          (us.enableAccrual === true && (us.customAccrualRate !== undefined || us.customAccrualFrequency !== undefined));
        const isAnnuallyWithAccrualDisabled = us.customAccrualFrequency === 'Annually' && us.enableAccrual === false;

        if (hasCustomAccrual && !isAnnuallyWithAccrualDisabled) {
          if (us.leaveTypeId) {
            settingsFromUserSettings[us.leaveTypeId] = {
              leaveTypeId: us.leaveTypeId,
              customAccrualRate: us.customAccrualRate,
              customAccrualFrequency: us.customAccrualFrequency,
              customAccrualCap: us.customAccrualCap,
              accrualStartFrequency: us.accrualStartFrequency,
              accrualEndDate: us.accrualEndDate,
              customAccrualRateDivision: us.customAccrualRateDivision, // May come from userSettings
              customAccrualCapDivision: us.customAccrualCapDivision, // May come from userSettings
            };

            // Initialize unit states from userSettings if available
            // Handle both integer (0/1) and string ("Days"/"Hours") values
            if (setCustomAccrualRateUnits && us.customAccrualRateDivision !== undefined && us.customAccrualRateDivision !== null) {
              const rateDivision = us.customAccrualRateDivision;
              const isHours = (typeof rateDivision === 'number' && rateDivision === 1) ||
                (typeof rateDivision === 'string' && (rateDivision === 'Hours' || rateDivision.toLowerCase() === 'hours'));
              setCustomAccrualRateUnits(prev => ({
                ...prev,
                [us.leaveTypeId]: isHours ? 'Hours' : 'Days'
              }));
            }

            if (setCustomAccrualCapUnits && us.customAccrualCapDivision !== undefined && us.customAccrualCapDivision !== null) {
              const capDivision = us.customAccrualCapDivision;
              const isHours = (typeof capDivision === 'number' && capDivision === 1) ||
                (typeof capDivision === 'string' && (capDivision === 'Hours' || capDivision.toLowerCase() === 'hours'));
              setCustomAccrualCapUnits(prev => ({
                ...prev,
                [us.leaveTypeId]: isHours ? 'Hours' : 'Days'
              }));
            }
          }
        }
      });

      // If we found custom settings from userSettings, set them immediately
      if (Object.keys(settingsFromUserSettings).length > 0) {
        setCustomAccrualSettings(prev => ({ ...prev, ...settingsFromUserSettings }));
      }

      // Fetch custom accrual settings for:
      // 1. Leave types that have enableAccrual in default settings
      // 2. Leave types where user has custom accrual (isCustomAccrualLeave: true)
      const defaultAccrualLeaveTypeIds = allLeaveTypes
        .filter(lt => lt.enableAccrual === true)
        .map(lt => lt.id);

      const customAccrualLeaveTypeIds = userSettings
        .filter(us => {
          const hasCustomAccrual = us.isCustomAccrualLeave === true || us.enableAccrual === true;
          const isAnnuallyWithAccrualDisabled = us.customAccrualFrequency === 'Annually' && us.enableAccrual === false;
          return hasCustomAccrual && !isAnnuallyWithAccrualDisabled;
        })
        .map(us => us.leaveTypeId)
        .filter(id => id !== undefined);

      // Combine and deduplicate leave type IDs
      const leaveTypeIds = [...new Set([...defaultAccrualLeaveTypeIds, ...customAccrualLeaveTypeIds])];

      if (leaveTypeIds.length > 0) {
        dispatch(onGetAccrualSettings(userId, leaveTypeIds))
          .then(result => {
            if (result.success && result.data && Array.isArray(result.data)) {
              const settingsMap = { ...settingsFromUserSettings }; // Start with settings from userSettings
              result.data.forEach(settings => {
                if (settings) {
                  // Backend returns leaveTypesId (with 's'), map it to leaveTypeId
                  const leaveTypeId = settings.leaveTypesId || settings.leaveTypeId;
                  if (leaveTypeId) {
                    settingsMap[leaveTypeId] = {
                      ...settings,
                      leaveTypeId: leaveTypeId, // Normalize to leaveTypeId
                      // Map accrualStartType to accrualStartFrequency if needed
                      accrualStartFrequency: settings.accrualStartFrequency || settings.customAccrualFrequency,
                      accrualStartType: settings.accrualStartType,
                    };

                    // Initialize unit states from backend data
                    // Handle both integer (0/1) and string ("Days"/"Hours") values
                    // CustomAccrualRateDivision defaults to 'Days' if not provided
                    if (setCustomAccrualRateUnits && settings.customAccrualRateDivision !== undefined && settings.customAccrualRateDivision !== null) {
                      const rateDivision = settings.customAccrualRateDivision;
                      const isHours = (typeof rateDivision === 'number' && rateDivision === 1) ||
                        (typeof rateDivision === 'string' && (rateDivision === 'Hours' || rateDivision.toLowerCase() === 'hours'));
                      setCustomAccrualRateUnits(prev => ({
                        ...prev,
                        [leaveTypeId]: isHours ? 'Hours' : 'Days'
                      }));
                    }

                    // CustomAccrualCapDivision defaults to 'Days' if not provided
                    if (setCustomAccrualCapUnits && settings.customAccrualCapDivision !== undefined && settings.customAccrualCapDivision !== null) {
                      const capDivision = settings.customAccrualCapDivision;
                      const isHours = (typeof capDivision === 'number' && capDivision === 1) ||
                        (typeof capDivision === 'string' && (capDivision === 'Hours' || capDivision.toLowerCase() === 'hours'));
                      setCustomAccrualCapUnits(prev => ({
                        ...prev,
                        [leaveTypeId]: isHours ? 'Hours' : 'Days'
                      }));
                    }
                  }
                }
              });
              setCustomAccrualSettings(settingsMap);

              // Auto-expand Custom Accrual Settings for leave types with custom settings
              // This will be handled in the main component via useEffect
            }
          });
      } else if (Object.keys(settingsFromUserSettings).length > 0) {
        // If no API call needed but we have settings from userSettings, ensure they're set
        setCustomAccrualSettings(settingsFromUserSettings);
      }
    }
  }, [mode, allLeaveTypes, userStore.users?.id, dispatch]);

  React.useEffect(() => {
    // Create a stable key from custom settings to prevent infinite loops
    const customSettingsKeys = Object.keys(customAccrualSettings).sort().join(',');
    const customSettingsKey = customSettingsKeys || 'none';
    const hasCustomSettings = Object.keys(customAccrualSettings).length > 0;

    // Only re-run if:
    // 1. Not initialized yet, OR
    // 2. Has custom settings that haven't been processed yet
    const shouldRun = mode === "edit" &&
      allLeaveTypes &&
      allLeaveTypes.length > 0 &&
      (!initializedRef.current || (hasCustomSettings && !customSettingsProcessedRef.current.has(customSettingsKey)));

    if (shouldRun) {
      // Mark these custom settings as processed
      if (hasCustomSettings) {
        customSettingsProcessedRef.current.add(customSettingsKey);
      }

      const activeUserSettings = userSettings.filter((s) => s.active !== false);
      // Get leaveBalance from backend (inactive records)
      const leaveBalance = userStore.users?.leaveBalance || [];
      const mergedSettings = allLeaveTypes.map((leaveType) => {
        // First check all userSettings (including inactive) to get the active value
        const allUserSetting = userSettings.find(
          (s) => s.leaveTypeId === leaveType.id
        );
        // Then check activeUserSettings for the actual setting data
        const userSetting = activeUserSettings.find(
          (s) => s.leaveTypeId === leaveType.id
        );
        // Sum utilized from inactive leaveBalance records for this leaveTypeId
        const utilized = leaveBalance
          .filter(
            (b) =>
              b.leaveTypesId === leaveType.id &&
              (b.active === false || b.active === undefined)
          )
          .reduce((sum, b) => sum + (b.value || 0), 0);
        if (userSetting) {
          // Check if user has custom accrual settings (from backend response)
          // Exclude cases where frequency is "Annually" and enableAccrual is false (Front-loaded Annual Grant)
          const hasCustomAccrualFromUser = (userSetting.isCustomAccrualLeave === true ||
            (userSetting.enableAccrual === true && (userSetting.customAccrualRate !== undefined || userSetting.customAccrualFrequency !== undefined))) &&
            !(userSetting.customAccrualFrequency === 'Annually' && userSetting.enableAccrual === false);

          // Determine enableAccrual: prioritize user's custom setting value, then fetched custom settings, then leave type default
          // IMPORTANT: Preserve the actual boolean value from userSetting, don't always default to true
          // If leave is inactive (active === false), enableAccrual must also be false
          let finalEnableAccrual = hasCustomAccrualFromUser
            ? (userSetting.enableAccrual !== undefined && userSetting.enableAccrual !== null
              ? userSetting.enableAccrual
              : true)  // Only default to true if not explicitly set
            : (customAccrualSettings[leaveType.id]
              ? true
              : (leaveType.enableAccrual !== undefined ? leaveType.enableAccrual : (leaveType.accrualFrequency && leaveType.accrualFrequency !== null)));

          // If leave is inactive, disable accrual
          if (userSetting.active === false) {
            finalEnableAccrual = false;
          }

          // Determine if custom accrual settings exist
          // Exclude cases where it's Annually with enableAccrual false (Front-loaded Annual Grant - use defaults)
          const hasCustomSettings = hasCustomAccrualFromUser || (!!customAccrualSettings[leaveType.id] &&
            !(customAccrualSettings[leaveType.id].customAccrualFrequency === 'Annually' &&
              (userSetting?.enableAccrual === false || customAccrualSettings[leaveType.id].enableAccrual === false)));

          // Priority for accrual values: 1) userSetting custom values, 2) fetched customAccrualSettings, 3) leaveType defaults
          const finalAccrualRate = userSetting.customAccrualRate !== undefined && userSetting.customAccrualRate !== null
            ? userSetting.customAccrualRate
            : (customAccrualSettings[leaveType.id]?.customAccrualRate !== undefined && customAccrualSettings[leaveType.id]?.customAccrualRate !== null
              ? customAccrualSettings[leaveType.id].customAccrualRate
              : (leaveType.accrualRate || 0));

          const finalAccrualFrequency = userSetting.customAccrualFrequency !== undefined && userSetting.customAccrualFrequency !== null
            ? userSetting.customAccrualFrequency
            : (customAccrualSettings[leaveType.id]?.customAccrualFrequency !== undefined && customAccrualSettings[leaveType.id]?.customAccrualFrequency !== null
              ? customAccrualSettings[leaveType.id].customAccrualFrequency
              : (leaveType.accrualFrequency || (finalEnableAccrual ? 'Annually' : null)));

          const finalAccrualCap = userSetting.customAccrualCap !== undefined && userSetting.customAccrualCap !== null
            ? userSetting.customAccrualCap
            : (customAccrualSettings[leaveType.id]?.customAccrualCap !== undefined && customAccrualSettings[leaveType.id]?.customAccrualCap !== null
              ? customAccrualSettings[leaveType.id].customAccrualCap
              : (leaveType.accrualCap || 0));

          const finalAccrualStartFrequency = userSetting.accrualStartFrequency !== undefined && userSetting.accrualStartFrequency !== null
            ? userSetting.accrualStartFrequency
            : (customAccrualSettings[leaveType.id]?.accrualStartFrequency || leaveType.accrualFrequency || 'Annually');

          const finalAccrualEndDate = userSetting.accrualEndDate !== undefined && userSetting.accrualEndDate !== null
            ? userSetting.accrualEndDate
            : (customAccrualSettings[leaveType.id]?.accrualEndDate || null);

          return {
            ...userSetting,
            id: leaveType.id,
            leaveTypeId: leaveType.id,
            name: leaveType.name,
            utilized,
            deducted: leaveType.deducted !== undefined ? leaveType.deducted : (leaveType.takeFromAllowance !== undefined ? leaveType.takeFromAllowance : false),
            // Accrual fields - prioritize user's custom settings, then fetched custom settings, then leave type defaults
            enableAccrual: finalEnableAccrual,
            accrualRate: finalAccrualRate,
            accrualFrequency: finalAccrualFrequency,
            accrualCap: finalAccrualCap,
            enableProration: leaveType.enableProration !== undefined ? leaveType.enableProration : true,
            accrualStartFrequency: finalAccrualStartFrequency,
            accrualEndDate: finalAccrualEndDate,
            tieredRates: leaveType.accrualTiers || leaveType.tieredRates || [],
            hasCustomAccrualSettings: hasCustomSettings,
            accruedBalance: userSetting.accruedBalance || 0,
          };
        } else {
          // Read active from userleaveSettings if available
          // If user has never had this leave type configured (allUserSetting is undefined),
          // default to false (inactive) instead of using global leaveType.active
          // This ensures new globally active leave types don't automatically become active for existing users
          const activeValue = allUserSetting?.active !== undefined
            ? allUserSetting.active
            : false;
          // For inactive leaves, preserve the value from allUserSetting if it exists
          const value = allUserSetting?.value !== undefined && allUserSetting?.value !== null
            ? allUserSetting.value
            : 0;
          const type = allUserSetting?.type || "Days";

          // Check if user has custom accrual settings - prioritize user's previous settings over global changes
          // This preserves user's previous custom accrual settings even if global leave type settings have changed
          // Exclude cases where frequency is "Annually" and enableAccrual is false (Front-loaded Annual Grant - use defaults)
          const hasUserCustomAccrual = (allUserSetting?.isCustomAccrualLeave === true ||
            (allUserSetting?.enableAccrual === true &&
              (allUserSetting?.customAccrualRate !== undefined ||
                allUserSetting?.customAccrualFrequency !== undefined ||
                allUserSetting?.customAccrualCap !== undefined))) &&
            !(allUserSetting?.customAccrualFrequency === 'Annually' && allUserSetting?.enableAccrual === false);

          // Priority for accrual values: 
          // 1) User's custom settings from allUserSetting (preserves previous settings even if global changed)
          // 2) Fetched customAccrualSettings (from API)
          // 3) Global leaveType defaults
          // If leave is inactive (active === false), enableAccrual must also be false
          let finalEnableAccrual = hasUserCustomAccrual
            ? (allUserSetting.enableAccrual !== undefined && allUserSetting.enableAccrual !== null
              ? allUserSetting.enableAccrual
              : true)
            : (customAccrualSettings[leaveType.id]
              ? true
              : (leaveType.enableAccrual !== undefined ? leaveType.enableAccrual : (leaveType.accrualFrequency && leaveType.accrualFrequency !== null)));

          // If leave is inactive, disable accrual
          if (activeValue === false) {
            finalEnableAccrual = false;
          }

          const finalAccrualRate = allUserSetting?.customAccrualRate !== undefined && allUserSetting?.customAccrualRate !== null
            ? allUserSetting.customAccrualRate
            : (customAccrualSettings[leaveType.id]?.customAccrualRate !== undefined && customAccrualSettings[leaveType.id]?.customAccrualRate !== null
              ? customAccrualSettings[leaveType.id].customAccrualRate
              : (leaveType.accrualRate || 0));

          const finalAccrualFrequency = allUserSetting?.customAccrualFrequency !== undefined && allUserSetting?.customAccrualFrequency !== null
            ? allUserSetting.customAccrualFrequency
            : (customAccrualSettings[leaveType.id]?.customAccrualFrequency !== undefined && customAccrualSettings[leaveType.id]?.customAccrualFrequency !== null
              ? customAccrualSettings[leaveType.id].customAccrualFrequency
              : (leaveType.accrualFrequency || (leaveType.enableAccrual ? 'Annually' : null)));

          const finalAccrualCap = allUserSetting?.customAccrualCap !== undefined && allUserSetting?.customAccrualCap !== null
            ? allUserSetting.customAccrualCap
            : (customAccrualSettings[leaveType.id]?.customAccrualCap !== undefined && customAccrualSettings[leaveType.id]?.customAccrualCap !== null
              ? customAccrualSettings[leaveType.id].customAccrualCap
              : (leaveType.accrualCap || 0));

          const finalAccrualStartFrequency = allUserSetting?.accrualStartFrequency !== undefined && allUserSetting?.accrualStartFrequency !== null
            ? allUserSetting.accrualStartFrequency
            : (customAccrualSettings[leaveType.id]?.accrualStartFrequency || leaveType.accrualFrequency || 'Annually');

          const finalAccrualEndDate = allUserSetting?.accrualEndDate !== undefined && allUserSetting?.accrualEndDate !== null
            ? allUserSetting.accrualEndDate
            : (customAccrualSettings[leaveType.id]?.accrualEndDate || null);

          return {
            id: leaveType.id,
            leaveTypeId: leaveType.id,
            balanceLeaveId: allUserSetting?.leaveBalanceId || allUserSetting?.balanceLeaveId || null,
            name: leaveType.name,
            value: value,
            type: type,
            active: activeValue,
            utilized,
            deducted: leaveType.deducted !== undefined ? leaveType.deducted : (leaveType.takeFromAllowance !== undefined ? leaveType.takeFromAllowance : false),
            // Use the final calculated accrual values that prioritize user's previous custom settings
            enableAccrual: finalEnableAccrual,
            accrualRate: finalAccrualRate,
            accrualFrequency: finalAccrualFrequency,
            accrualCap: finalAccrualCap,
            enableProration: leaveType.enableProration !== undefined ? leaveType.enableProration : true,
            accrualStartFrequency: finalAccrualStartFrequency,
            accrualEndDate: finalAccrualEndDate,
            tieredRates: leaveType.accrualTiers || leaveType.tieredRates || [],
            // Mark as having custom settings if user had them previously, even if global settings changed
            // Exclude cases where it's Annually with enableAccrual false (Front-loaded Annual Grant - use defaults)
            hasCustomAccrualSettings: hasUserCustomAccrual || (!!customAccrualSettings[leaveType.id] &&
              !(customAccrualSettings[leaveType.id].customAccrualFrequency === 'Annually' &&
                (allUserSetting?.enableAccrual === false || customAccrualSettings[leaveType.id].enableAccrual === false))),
            accruedBalance: allUserSetting?.accruedBalance || 0,
          };
        }
      });
      setFieldValue("userLeaveSettings", mergedSettings);
      dispatch(
        users({
          ...userStore.users,
          userLeaveSettings: mergedSettings,
        })
      );
      // Only set initialized to true on first initialization
      // This allows re-initialization when custom settings are loaded
      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    allLeaveTypes?.length,
    userSettings?.length,
    // Use a stable string representation of custom settings keys instead of the object
    // This prevents infinite loops when the object reference changes but keys don't
    Object.keys(customAccrualSettings || {}).sort().join(','),
  ]);
  return null;
}

const UserForm = (props) => {
  const { userId, mode = "create" } = props.location.state || {};
  const { t } = useTranslation();
  const additionalProfileValidationSchema = useMemo(
    () => createAdditionalProfileValidationSchema(t),
    [t]
  );
  const userFormValidationSchema = useMemo(
    () => createUserFormValidationSchema(t, mode, additionalProfileValidationSchema),
    [t, mode, additionalProfileValidationSchema]
  );
  const { showWorkplaceRequired, workplaceRequiredModal, hasWorkplaces, workplacesLoading } =
    useWorkplaceGate("users");
  const dispatch = useDispatch();
  const userStore = useSelector((state) => state.userReducer);
  const officeStore = useSelector((state) => state.officeReducer);
  const leaveTypesStore = useSelector((state) => state.leaveTypeReducer);
  const designationList = useSelector((state) => state.designationReducer?.record || []);
  const employmentTypeList = useSelector((state) => state.employmentTypeReducer?.record || []);
  const teamList = useSelector((state) => state.teamReducer?.record || []);
  const initializedRef = React.useRef(false);
  const { isSuspended } = useSubscriptionStatus();
  // Store original values for each leave type to restore when reactivating
  const originalValuesRef = React.useRef({});
  // Track which prorated values have been set for Front-loaded leaves in create mode
  const proratedValuesSetRef = React.useRef(new Set());
  // Track previous leaveYearStart to detect changes
  const previousLeaveYearStartRef = React.useRef(null);

  // Function to generate a secure random password
  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    // Ensure at least one of each: lowercase, uppercase, number, special char
    password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
    password += "0123456789"[Math.floor(Math.random() * 10)];
    password += "!@#$%^&*"[Math.floor(Math.random() * 8)];
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Separate state for leave types
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(true); // Start as true to show loader initially
  // Separate loading state for the form (independent from userStore.isLoading)
  const [formLoading, setFormLoading] = useState(false);
  const [enrollOptions, setEnrollOptions] = useState([]);

  // State for Leave Approver dropdown
  const [approverSearchTerm, setApproverSearchTerm] = useState("");
  const [isApproverDropdownOpen, setIsApproverDropdownOpen] = useState(false);
  const approverDropdownRef = useRef(null);
  const approverInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // State for info popups
  const [showLeaveYearInfoPopup, setShowLeaveYearInfoPopup] = useState(false);
  const [showDeductibleInfoPopup, setShowDeductibleInfoPopup] = useState(false);
  const [showApproverManagerInfoPopup, setShowApproverManagerInfoPopup] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  // State for accrual customization UI
  const [customAccrualSettings, setCustomAccrualSettings] = useState({}); // { leaveTypeId: settings }
  const [expandedAccrualSettings, setExpandedAccrualSettings] = useState({}); // { leaveTypeId: true/false }
  const [expandedAdditionalSections, setExpandedAdditionalSections] = useState(
    DEFAULT_EXPANDED_ADDITIONAL_SECTIONS
  );
  const toggleAdditionalSection = (sectionKey) => {
    setExpandedAdditionalSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };
  const [editingAccrualSettings, setEditingAccrualSettings] = useState({}); // { leaveTypeId: { accrualFrequency, accrualRate, accrualCap, accrualStartFrequency, accrualEndDate } }
  // State for tracking units (Hours/Days) for custom accrual rate and cap
  const [customAccrualRateUnits, setCustomAccrualRateUnits] = useState({}); // { leaveTypeId: 'Days' | 'Hours' }
  const [customAccrualCapUnits, setCustomAccrualCapUnits] = useState({}); // { leaveTypeId: 'Days' | 'Hours' }
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Helper functions for days/hours conversion (8 hours = 1 day)
  const daysToHours = (days) => {
    if (!days || days === 0) return 0;
    return parseFloat((days * 8).toFixed(2));
  };

  const hoursToDays = (hours) => {
    if (!hours || hours === 0) return 0;
    return parseFloat((hours / 8).toFixed(2));
  };

  // Auto-expand Custom Accrual Settings when custom settings are detected
  React.useEffect(() => {
    if (mode === "edit" && Object.keys(customAccrualSettings).length > 0) {
      // Expand settings for all leave types that have custom settings
      const newExpanded = { ...expandedAccrualSettings };
      let hasChanges = false;

      Object.keys(customAccrualSettings).forEach(leaveTypeId => {
        if (!expandedAccrualSettings[leaveTypeId]) {
          newExpanded[leaveTypeId] = true;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setExpandedAccrualSettings(newExpanded);
      }
    }
  }, [customAccrualSettings, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [getUserInfo, setUserInfo] = useState({
    name: userStore.users?.name,
    email: userStore.users?.email,
    empStartDate: userStore.users?.empStartDate,
    role: userStore.users?.role,
    approvalId: userStore.users?.approvalId,
    userLeaveSettings: userStore.users?.userLeaveSettings || [],
    leaveYearStart: userStore.users?.leaveYearStart,
    departmentId: userStore.users?.departmentId,
    officeId: userStore.users?.officeId,
    designationId:
      userStore.users?.designationId ??
      userStore.users?.DesignationId ??
      "",
    employmentTypeId:
      userStore.users?.employmentTypeId ??
      userStore.users?.EmploymentTypeId ??
      "",
    teamIds: resolveTeamIdsFromUser(userStore.users),
    additionalProfile: resolveAdditionalProfileFromUser(userStore.users, officeStore.countries),
    password: "",
    isActive: userStore.users?.isActive !== false, // Default to true for new users (for User Status Management in edit mode)
    isLoginDisabled: userStore.users?.isLoginDisabled !== undefined ? userStore.users.isLoginDisabled : false, // Default to false (login enabled) for new users
    imageUrl: userStore.users?.imageUrl || "", // Add image URL field
  });

  // Profile image upload states
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [shouldUpdateImageUrl, setShouldUpdateImageUrl] = useState(false);
  const profileImageInputRef = useRef(null);
  const setFieldValueRef = useRef(null);

  // Image optimization function
  const optimizeImage = async (file) => {
    try {
      setIsOptimizing(true);
      setOptimizationProgress(0);

      console.log("Original file size:", file.size, "bytes");

      const options = {
        maxSizeMB: 1, // Maximum 1MB
        maxWidthOrHeight: 300, // Smaller size for profile images
        useWebWorker: true,
        fileType: "image/webp", // WebP: smaller files than JPEG at similar quality
        quality: 0.9, // Higher quality for profile images
        alwaysKeepResolution: false, // Allow resolution reduction for better compression
        onProgress: (progress) => {
          setOptimizationProgress(progress);
        },
      };

      const compressedFile = await imageCompression(file, options);

      setOptimizationProgress(100);
      setIsOptimizing(false);

      return compressedFile;
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("userFormImageOptimization", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Image optimization failed",
        });
      }
      setIsOptimizing(false);
      throw error;
    }
  };

  // Handle profile image upload
  const handleProfileImageChange = async (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      setImageUploadProgress(0);

      // Optimize the image
      const optimizedFile = await optimizeImage(file);

      // Generate unique filename
      const timestamp = new Date().getTime();
      const fileName = `user-profile-${timestamp}.webp`;

      // Get presigned URL
      const presignedUrlResponse = await getPreSignedUrl(fileName, "DP");
      console.log("Presigned URL Response:", presignedUrlResponse);

      // Handle different response structures
      let presignedUrl;
      if (presignedUrlResponse && presignedUrlResponse.data) {
        presignedUrl = presignedUrlResponse.data;
      } else if (
        presignedUrlResponse &&
        typeof presignedUrlResponse === "string"
      ) {
        presignedUrl = presignedUrlResponse;
      } else if (
        presignedUrlResponse &&
        presignedUrlResponse.success &&
        presignedUrlResponse.data
      ) {
        presignedUrl = presignedUrlResponse.data;
      } else {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("userFormPresignedUrl", {
            type: "exception",
            success: false,
            reason: "Invalid presigned URL response structure",
            response: presignedUrlResponse,
          });
        }
        throw new Error("Invalid presigned URL response");
      }

      if (!presignedUrl) {
        throw new Error("Failed to get presigned URL");
      }

      // Upload to S3
      const uploadedUrl = await uploadImgToS3(
        "PUT",
        presignedUrl.data,
        optimizedFile
      );

      // Update the Formik form values with the uploaded image URL
      setFieldValue("imageUrl", uploadedUrl);

      // In edit mode, immediately update the profile image in the backend
      if (mode === "edit" && userId) {
        console.log("🖼️ Updating profile image in backend for user:", userId);
        dispatch(onUpdateProfileImage(userId, uploadedUrl));
      } else {
        Common.showSuccessMessage("Profile image uploaded successfully!");
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("userFormProfileImageUpload", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Upload failed",
          status: error?.response?.status,
        });
      }
      Common.showErrorMessage(
        "Failed to upload profile image. Please try again."
      );
    } finally {
      setIsUploadingImage(false);
      setImageUploadProgress(0);
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    }
  };

  useEffect(() => {
    setUserInfo(userStore.users);
    // Set flag to update image URL if user data has imageUrl
    if (userStore.users?.imageUrl) {
      setShouldUpdateImageUrl(true);
    }
  }, [userStore.users]);

  useEffect(() => {
    dispatch(onGetUser(userId, mode));
  }, [mode, userId]);

  useEffect(() => {
    const options = { start: 0, page: 1, take: 500 };
    dispatch(designationActions.DesignationListing(options, 1));
    dispatch(employmentTypeActions.EmploymentTypeListing(options, 1));
    dispatch(teamActions.TeamListing(options, 1));
  }, [dispatch]);

  useEffect(() => {
    if (!officeStore.countries?.length) {
      dispatch(onGetCountries());
    }
  }, [dispatch, officeStore.countries?.length]);

  useEffect(() => {
    let cancelled = false;
    fetchEnrollOptions()
      .then((result) => {
        if (cancelled) return;
        const unclaimed = result?.unclaimedEnrollOptions || [];
        const all = result?.enrollOptions || [];
        setEnrollOptions(unclaimed.length ? unclaimed : all);
      })
      .catch(() => {
        if (!cancelled) setEnrollOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle image URL updates when user data is loaded
  useEffect(() => {
    if (
      shouldUpdateImageUrl &&
      userStore.users?.imageUrl &&
      setFieldValueRef.current
    ) {
      setFieldValueRef.current("imageUrl", userStore.users.imageUrl);
      setShouldUpdateImageUrl(false);
    }
  }, [shouldUpdateImageUrl, userStore.users?.imageUrl]);

  // Helper function to save accrual settings
  const saveAccrualSettingsForUser = async (userId, values, isUpdate = false) => {
    if (!userId || Object.keys(editingAccrualSettings).length === 0) {
      return;
    }

    // Save all custom accrual settings that were configured
    const accrualSavePromises = Object.keys(editingAccrualSettings).map(async (leaveTypeId) => {
      const settings = editingAccrualSettings[leaveTypeId];
      if (!settings) return null;

      // Get the leave type to get defaults
      const leave = values.userLeaveSettings.find(l => (l.leaveTypeId || l.id) === parseInt(leaveTypeId, 10));
      if (!leave) return null;

      // Get the default leave type from store to get AccrualRateDivision and AccrualCapDivision
      const defaultLeaveType = leaveTypesStore.leaveTypes?.find(lt => lt.id === parseInt(leaveTypeId, 10));

      const enableAccrual = settings?.enableAccrual !== undefined
        ? settings.enableAccrual !== false
        : (leave.enableAccrual !== false);

      const customAccrualRate = settings?.customAccrualRate !== undefined && settings?.customAccrualRate !== null
        ? parseFloat(settings.customAccrualRate)
        : (leave.accrualRate ? parseFloat(leave.accrualRate) : null);

      const customAccrualFrequency = settings?.customAccrualFrequency || leave.accrualFrequency || null;

      const customAccrualCap = settings?.customAccrualCap !== undefined && settings?.customAccrualCap !== null
        ? parseFloat(settings.customAccrualCap)
        : (leave.accrualCap ? parseFloat(leave.accrualCap) : null);

      const accrualStartFrequency = customAccrualFrequency || 'Annually';

      // Get AccrualStartType from settings or leave, default to LeaveYearStart
      const accrualStartType = settings?.accrualStartType || leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START;

      const accrualEndDateValue = settings?.accrualEndDate || leave.accrualEndDate || null;
      const accrualEndDate = accrualEndDateValue
        ? new Date(accrualEndDateValue).toISOString()
        : null;

      const leaveTypeIdInt = parseInt(leaveTypeId, 10);
      if (isNaN(leaveTypeIdInt)) return null;

      // Get the selected units (default to Days if not set)
      const customAccrualRateDivisionValue = customAccrualRateUnits[leaveTypeId] || 'Days';
      const customAccrualCapDivisionValue = customAccrualCapUnits[leaveTypeId] || 'Days';

      // Convert string values to integer enum: Days = 0, Hours = 1
      const customAccrualRateDivision = (typeof customAccrualRateDivisionValue === 'number')
        ? customAccrualRateDivisionValue  // Already an integer (0 or 1)
        : ((customAccrualRateDivisionValue === 'Hours' || customAccrualRateDivisionValue === 'hours')
          ? 1  // Hours
          : 0); // Days

      const customAccrualCapDivision = (typeof customAccrualCapDivisionValue === 'number')
        ? customAccrualCapDivisionValue  // Already an integer (0 or 1)
        : ((customAccrualCapDivisionValue === 'Hours' || customAccrualCapDivisionValue === 'hours')
          ? 1  // Hours
          : 0); // Days

      // Get default AccrualRateDivision and AccrualCapDivision from leave type
      const accrualRateDivisionValue = defaultLeaveType?.accrualRateDivision ?? leave.accrualRateDivision ?? 'Days';
      const accrualCapDivisionValue = defaultLeaveType?.accrualCapDivision ?? leave.accrualCapDivision ?? 'Days';

      // Convert default division values to integer enum: Days = 0, Hours = 1
      const accrualRateDivision = (typeof accrualRateDivisionValue === 'number')
        ? accrualRateDivisionValue  // Already an integer (0 or 1)
        : ((accrualRateDivisionValue === 'Hours' || accrualRateDivisionValue === 'hours' || accrualRateDivisionValue === 1)
          ? 1  // Hours
          : 0); // Days

      const accrualCapDivision = (typeof accrualCapDivisionValue === 'number')
        ? accrualCapDivisionValue  // Already an integer (0 or 1)
        : ((accrualCapDivisionValue === 'Hours' || accrualCapDivisionValue === 'hours' || accrualCapDivisionValue === 1)
          ? 1  // Hours
          : 0); // Days

      const saveData = {
        UserId: String(userId),
        LeaveTypeId: leaveTypeIdInt,
        EnableAccrual: enableAccrual,
        CustomAccrualRate: customAccrualRate,
        CustomAccrualFrequency: customAccrualFrequency,
        CustomAccrualCap: customAccrualCap,
        AccrualStartFrequency: accrualStartFrequency,
        AccrualStartType: accrualStartType,
        AccrualEndDate: accrualEndDate,
        CustomAccrualRateDivision: customAccrualRateDivision, // 0 for Days, 1 for Hours
        CustomAccrualCapDivision: customAccrualCapDivision, // 0 for Days, 1 for Hours
        AccrualRateDivision: accrualRateDivision, // 0 for Days, 1 for Hours (default leave type division)
        AccrualCapDivision: accrualCapDivision, // 0 for Days, 1 for Hours (default leave type division)
        Active: true
      };

      // Check if custom settings already exist (for update vs create)
      const hasExistingSettings = leave.hasCustomAccrualSettings || customAccrualSettings[leaveTypeId];

      // Use PUT for update, POST for create
      return dispatch(onSaveAccrualSettings(saveData, isUpdate ? hasExistingSettings : false));
    });

    // Wait for all accrual settings to be saved
    await Promise.all(accrualSavePromises.filter(p => p !== null));
  };

  const onSubmitBtnClick = async (values) => {
    setFormLoading(true);
    dispatch(isLoading(true));

    // Merge accrual settings from editingAccrualSettings into userLeaveSettings
    const mergedUserLeaveSettings = values.userLeaveSettings.map((leave) => {
      const leaveTypeId = leave.leaveTypeId || leave.id;
      const editingSettings = editingAccrualSettings[leaveTypeId];


      // Check if this is a Tiered leave
      const isTiered = leave.accrualFrequency === 'Tiered' ||
        leave.customAccrualFrequency === 'Tiered' ||
        leave.accrualStartFrequency === 'Tiered';

      // For Tiered leaves, always use the enableAccrual from the form state (updated by checkbox)
      if (isTiered) {
        // Use enableAccrual from form state (updated by checkbox) - explicitly check for boolean
        const finalEnableAccrual = leave.enableAccrual !== undefined && leave.enableAccrual !== null
          ? leave.enableAccrual
          : true;

        // If enableAccrual is true, active must also be true
        const finalActive = finalEnableAccrual === true
          ? true
          : (leave.active !== undefined && leave.active !== null ? leave.active : true);

        // Get the original leave type to determine defaults
        const originalLeave = leaveTypesStore.leaveTypes?.find(lt => lt.id === leaveTypeId);

        // Get default AccrualRateDivision and AccrualCapDivision from leave type
        const accrualRateDivisionValue = originalLeave?.accrualRateDivision ?? leave.accrualRateDivision ?? 'Days';
        const accrualCapDivisionValue = originalLeave?.accrualCapDivision ?? leave.accrualCapDivision ?? 'Days';

        // Convert default division values to integer enum: Days = 0, Hours = 1
        const accrualRateDivision = (typeof accrualRateDivisionValue === 'number')
          ? accrualRateDivisionValue  // Already an integer (0 or 1)
          : ((accrualRateDivisionValue === 'Hours' || accrualRateDivisionValue === 'hours' || accrualRateDivisionValue === 1)
            ? 1  // Hours
            : 0); // Days

        const accrualCapDivision = (typeof accrualCapDivisionValue === 'number')
          ? accrualCapDivisionValue  // Already an integer (0 or 1)
          : ((accrualCapDivisionValue === 'Hours' || accrualCapDivisionValue === 'hours' || accrualCapDivisionValue === 1)
            ? 1  // Hours
            : 0); // Days

        return {
          ...leave,
          // Set active based on enableAccrual: if accrual is enabled, leave must be active
          active: finalActive,
          accrualStartFrequency: leave.accrualStartFrequency || leave.accrualFrequency || 'Tiered',
          accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
          customAccrualRate: leave.customAccrualRate || leave.accrualRate || 0,
          customAccrualFrequency: leave.customAccrualFrequency || leave.accrualFrequency || 'Tiered',
          customAccrualCap: leave.customAccrualCap || leave.accrualCap || 0,
          accrualEndDate: leave.accrualEndDate || null,
          enableAccrual: finalEnableAccrual,
          // Division fields for default leave type settings (Tiered leaves use defaults)
          accrualRateDivision: accrualRateDivision, // 0 for Days, 1 for Hours
          accrualCapDivision: accrualCapDivision, // 0 for Days, 1 for Hours
          // For Tiered leaves, custom divisions default to Days (0)
          customAccrualRateDivision: 0, // Days (default for Tiered)
          customAccrualCapDivision: 0, // Days (default for Tiered)
        };
      }

      // If there are editing settings, merge them
      if (editingSettings) {
        // Get the original leave type to determine defaults
        const originalLeave = leaveTypesStore.leaveTypes?.find(lt => lt.id === leaveTypeId);

        // Determine values - use editing settings if provided, otherwise use leave defaults
        // Note: This will be overridden below if frequency is "Annually"

        const customAccrualFrequency = editingSettings?.customAccrualFrequency || leave.accrualFrequency || null;

        // If frequency is "Annually", clear custom accrual settings (Annual Grant doesn't use accrual)
        const isAnnually = customAccrualFrequency === 'Annually';
        // Use enableAccrual from editingSettings if explicitly set, otherwise use leave.enableAccrual from form state
        // Preserve the actual boolean value (true/false), not just check if not false
        const finalEnableAccrual = isAnnually
          ? false
          : (editingSettings?.enableAccrual !== undefined && editingSettings?.enableAccrual !== null
            ? editingSettings.enableAccrual  // Use the actual boolean value
            : (leave.enableAccrual !== undefined && leave.enableAccrual !== null
              ? leave.enableAccrual
              : true));
        const finalCustomAccrualRate = isAnnually ? 0 : (editingSettings?.customAccrualRate !== undefined && editingSettings?.customAccrualRate !== null
          ? parseFloat(editingSettings.customAccrualRate)
          : (leave.accrualRate ? parseFloat(leave.accrualRate) : 0));
        const finalCustomAccrualCap = isAnnually ? 0 : (editingSettings?.customAccrualCap !== undefined && editingSettings?.customAccrualCap !== null
          ? parseFloat(editingSettings.customAccrualCap)
          : (leave.accrualCap ? parseFloat(leave.accrualCap) : 0));

        // AccrualStartFrequency comes from the customAccrualFrequency (the accrual frequency)
        const accrualStartFrequency = customAccrualFrequency || 'Annually';

        // Get AccrualStartType from editing settings or leave, default to LeaveYearStart
        const accrualStartType = editingSettings?.accrualStartType || leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START;

        // AccrualEndDate - convert date string to ISO format or null
        const accrualEndDateValue = editingSettings?.accrualEndDate || leave.accrualEndDate || null;
        const accrualEndDate = accrualEndDateValue
          ? new Date(accrualEndDateValue).toISOString()
          : null;

        // Get division values from state (customAccrualRateUnits and customAccrualCapUnits)
        // These are set when user selects Days/Hours radio buttons
        const customAccrualRateDivisionValue = customAccrualRateUnits[leaveTypeId] || 'Days';
        const customAccrualCapDivisionValue = customAccrualCapUnits[leaveTypeId] || 'Days';

        // Convert string values to integer enum: Days = 0, Hours = 1
        const customAccrualRateDivision = (typeof customAccrualRateDivisionValue === 'number')
          ? customAccrualRateDivisionValue  // Already an integer (0 or 1)
          : ((customAccrualRateDivisionValue === 'Hours' || customAccrualRateDivisionValue === 'hours')
            ? 1  // Hours
            : 0); // Days

        const customAccrualCapDivision = (typeof customAccrualCapDivisionValue === 'number')
          ? customAccrualCapDivisionValue  // Already an integer (0 or 1)
          : ((customAccrualCapDivisionValue === 'Hours' || customAccrualCapDivisionValue === 'hours')
            ? 1  // Hours
            : 0); // Days

        // Get default AccrualRateDivision and AccrualCapDivision from leave type
        const accrualRateDivisionValue = originalLeave?.accrualRateDivision ?? leave.accrualRateDivision ?? 'Days';
        const accrualCapDivisionValue = originalLeave?.accrualCapDivision ?? leave.accrualCapDivision ?? 'Days';

        // Convert default division values to integer enum: Days = 0, Hours = 1
        const accrualRateDivision = (typeof accrualRateDivisionValue === 'number')
          ? accrualRateDivisionValue  // Already an integer (0 or 1)
          : ((accrualRateDivisionValue === 'Hours' || accrualRateDivisionValue === 'hours' || accrualRateDivisionValue === 1)
            ? 1  // Hours
            : 0); // Days

        const accrualCapDivision = (typeof accrualCapDivisionValue === 'number')
          ? accrualCapDivisionValue  // Already an integer (0 or 1)
          : ((accrualCapDivisionValue === 'Hours' || accrualCapDivisionValue === 'hours' || accrualCapDivisionValue === 1)
            ? 1  // Hours
            : 0); // Days

        // For non-Annually frequencies, bind active with enableAccrual
        // If enableAccrual is false, active should also be false
        // If enableAccrual is true, active should also be true
        // For Annually frequency, use the active value from form state
        const finalActive = isAnnually
          ? (leave.active !== undefined && leave.active !== null ? leave.active : true)
          : finalEnableAccrual; // For non-Annually, active = enableAccrual

        return {
          ...leave,
          // Set active based on enableAccrual for non-Annually frequencies
          active: finalActive,
          // Add accrual properties
          enableAccrual: finalEnableAccrual,
          accrualRate: finalCustomAccrualRate,
          accrualCap: finalCustomAccrualCap,
          accrualStartFrequency: accrualStartFrequency,
          accrualStartType: accrualStartType,
          customAccrualRate: finalCustomAccrualRate,
          customAccrualFrequency: customAccrualFrequency,
          customAccrualCap: finalCustomAccrualCap,
          accrualEndDate: accrualEndDate,
          // Division fields for custom accrual settings
          customAccrualRateDivision: customAccrualRateDivision, // 0 for Days, 1 for Hours
          customAccrualCapDivision: customAccrualCapDivision, // 0 for Days, 1 for Hours
          // Division fields for default leave type settings
          accrualRateDivision: accrualRateDivision, // 0 for Days, 1 for Hours
          accrualCapDivision: accrualCapDivision, // 0 for Days, 1 for Hours
        };
      }

      // If no editing settings, check if leave has accrual properties and include them
      // Use enableAccrual from form state - preserve the actual value (true/false) not just check if not false
      const finalEnableAccrual = leave.enableAccrual !== undefined && leave.enableAccrual !== null
        ? leave.enableAccrual
        : true;

      // Check if this is a non-deductible allowance
      const isNonDeductible = leave.deducted === false;

      // Check if frequency is Annually
      const isAnnually = (leave.accrualFrequency === 'Annually' || leave.customAccrualFrequency === 'Annually');

      // Get the original leave type to determine defaults
      const originalLeave = leaveTypesStore.leaveTypes?.find(lt => lt.id === leaveTypeId);

      // Get division values from state (customAccrualRateUnits and customAccrualCapUnits)
      // These are set when user selects Days/Hours radio buttons
      const customAccrualRateDivisionValue = customAccrualRateUnits[leaveTypeId] || 'Days';
      const customAccrualCapDivisionValue = customAccrualCapUnits[leaveTypeId] || 'Days';

      // Convert string values to integer enum: Days = 0, Hours = 1
      const customAccrualRateDivision = (typeof customAccrualRateDivisionValue === 'number')
        ? customAccrualRateDivisionValue  // Already an integer (0 or 1)
        : ((customAccrualRateDivisionValue === 'Hours' || customAccrualRateDivisionValue === 'hours')
          ? 1  // Hours
          : 0); // Days

      const customAccrualCapDivision = (typeof customAccrualCapDivisionValue === 'number')
        ? customAccrualCapDivisionValue  // Already an integer (0 or 1)
        : ((customAccrualCapDivisionValue === 'Hours' || customAccrualCapDivisionValue === 'hours')
          ? 1  // Hours
          : 0); // Days

      // Get default AccrualRateDivision and AccrualCapDivision from leave type
      const accrualRateDivisionValue = originalLeave?.accrualRateDivision ?? leave.accrualRateDivision ?? 'Days';
      const accrualCapDivisionValue = originalLeave?.accrualCapDivision ?? leave.accrualCapDivision ?? 'Days';

      // Convert default division values to integer enum: Days = 0, Hours = 1
      const accrualRateDivision = (typeof accrualRateDivisionValue === 'number')
        ? accrualRateDivisionValue  // Already an integer (0 or 1)
        : ((accrualRateDivisionValue === 'Hours' || accrualRateDivisionValue === 'hours' || accrualRateDivisionValue === 1)
          ? 1  // Hours
          : 0); // Days

      const accrualCapDivision = (typeof accrualCapDivisionValue === 'number')
        ? accrualCapDivisionValue  // Already an integer (0 or 1)
        : ((accrualCapDivisionValue === 'Hours' || accrualCapDivisionValue === 'hours' || accrualCapDivisionValue === 1)
          ? 1  // Hours
          : 0); // Days

      // For non-deductible allowances, use active value directly from form state (not bound to enableAccrual)
      // For non-Annually frequencies (deductible), bind active with enableAccrual
      // For Annually frequency, use the active value from form state
      const finalActive = isNonDeductible
        ? (leave.active !== undefined && leave.active !== null ? leave.active : true) // Non-deductible: use form state directly
        : (isAnnually
          ? (leave.active !== undefined && leave.active !== null ? leave.active : true) // Annually: use form state
          : finalEnableAccrual); // Non-Annually deductible: active = enableAccrual

      return {
        ...leave,
        // Set active based on leave type (non-deductible uses form state, deductible binds to enableAccrual)
        active: finalActive,
        // Include accrual properties from leave if they exist
        accrualStartFrequency: leave.accrualFrequency || 'Annually',
        accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
        customAccrualRate: leave.accrualRate || 0,
        customAccrualFrequency: leave.accrualFrequency || null,
        customAccrualCap: leave.accrualCap || 0,
        accrualEndDate: leave.accrualEndDate || null,
        enableAccrual: finalEnableAccrual,
        // Division fields for custom accrual settings
        customAccrualRateDivision: customAccrualRateDivision, // 0 for Days, 1 for Hours
        customAccrualCapDivision: customAccrualCapDivision, // 0 for Days, 1 for Hours
        // Division fields for default leave type settings
        accrualRateDivision: accrualRateDivision, // 0 for Days, 1 for Hours
        accrualCapDivision: accrualCapDivision, // 0 for Days, 1 for Hours
      };
    });

    // Create updated values with merged accrual settings
    const updatedValues = {
      ...values,
      userLeaveSettings: mergedUserLeaveSettings,
    };

    if (mode === "create") {
      try {
        // Create the user with accrual settings included
        const createResult = await dispatch(onRegisterByAdmin(updatedValues));
        if (createResult?.success) {
          // Update office dashboard employee count for the office the user was added to
          if (updatedValues.officeId) {
            dispatch(incrementOfficeUserCount(updatedValues.officeId));
          }
          props.history.push("/users");
        }
      } catch (error) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("userFormCreateUser", {
            type: "exception",
            success: false,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
            status: error?.response?.status,
          });
        }
      } finally {
        setFormLoading(false);
        dispatch(isLoading(false));
      }
    } else {
      // Edit mode - update user with accrual settings included
      try {
        const editResult = await dispatch(onEditUser(updatedValues, userId));
        if (editResult?.success) {
          // No need for separate accrual settings API call
        }
      } catch (error) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("userFormUpdateUser", {
            type: "exception",
            success: false,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
            status: error?.response?.status,
          });
        }
      } finally {
        setFormLoading(false);
        dispatch(isLoading(false));
      }
    }
  };

  const dropdownsLoadedOnceRef = useRef(false);

  // Fetch dropdown data (admin roles, offices, departments, leave types) once on mount to avoid double API calls
  useEffect(() => {
    if (dropdownsLoadedOnceRef.current) return;
    dropdownsLoadedOnceRef.current = true;
    setLeaveTypesLoading(true);
    dispatch(onGetAdminRoles());
    dispatch(onGetOffices());
    dispatch(onGetDepartments());
    dispatch(onGetLeaveTypes());
  }, [dispatch]);

  // Fetch approvers when we have a user id (edit: after user loads; create: from props if any)
  useEffect(() => {
    const currentUserId = userId || (mode === "edit" ? userStore.users?.id : null);
    dispatch(onGetApprovers(currentUserId));
  }, [dispatch, userId, mode, userStore.users?.id]);

  // Sync leave types from Redux store to local state
  useEffect(() => {
    // Update local state when Redux store changes
    if (
      leaveTypesStore?.leaveTypes &&
      Array.isArray(leaveTypesStore.leaveTypes) &&
      leaveTypesStore.leaveTypes.length > 0
    ) {
      setLeaveTypes(leaveTypesStore.leaveTypes);
      setLeaveTypesLoading(false);
    } else if (leaveTypesStore?.loading === false) {
      // Only set loading to false if the store explicitly says loading is done
      // This handles the case where leave types are empty but loading is complete
      setLeaveTypes(leaveTypesStore?.leaveTypes || []);
      setLeaveTypesLoading(false);
    } else if (leaveTypesStore?.loading === true) {
      // Keep loading state true while fetching
      setLeaveTypesLoading(true);
    } else if (!leaveTypesStore || !leaveTypesStore.hasOwnProperty('loading')) {
      // If leaveTypesStore is undefined or doesn't have loading property,
      // it means the store hasn't been initialized yet, so keep loading state
      // This handles the case when navigating from users.js while it's still loading
      setLeaveTypesLoading(true);
    }
  }, [leaveTypesStore, mode]);

  // Also use leave types from user data if available (for edit mode)
  useEffect(() => {
    if (
      mode === "edit" &&
      userStore.users?.allLeaveTypes &&
      Array.isArray(userStore.users.allLeaveTypes)
    ) {
      console.log(
        "🔍 Using leave types from user data:",
        userStore.users.allLeaveTypes
      );
      setLeaveTypes(userStore.users.allLeaveTypes);
      setLeaveTypesLoading(false);
    }
  }, [userStore.users?.allLeaveTypes, mode]);

  // Reset initialization flag when mode or user changes
  useEffect(() => {
    initializedRef.current = false;
    // Reset prorated values tracking when mode changes
    proratedValuesSetRef.current.clear();
  }, [mode, userStore.users?.id]);

  // Reset initialization flag when leave types are loaded for new users only
  useEffect(() => {
    if (mode !== "edit" && leaveTypes.length > 0) {
      initializedRef.current = false;
    }
  }, [leaveTypes, mode]);

  // Close approver dropdown when clicking outside and update position on scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the input and the dropdown menu
      const isClickInsideInput = approverInputRef.current && approverInputRef.current.contains(event.target);
      const isClickInsideDropdown = event.target.closest('.approver-dropdown-menu');
      const isClickInsideContainer = approverDropdownRef.current && approverDropdownRef.current.contains(event.target);

      if (!isClickInsideInput && !isClickInsideDropdown && !isClickInsideContainer) {
        setIsApproverDropdownOpen(false);
        setApproverSearchTerm("");
      }
    };

    const handleScroll = () => {
      if (isApproverDropdownOpen && approverInputRef.current) {
        const rect = approverInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 2,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    const handleResize = () => {
      if (isApproverDropdownOpen && approverInputRef.current) {
        const rect = approverInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 2,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isApproverDropdownOpen]);

  // Filter approvers based on search term
  const filteredApprovers = useMemo(() => {
    if (!userStore.approvers || !Array.isArray(userStore.approvers)) return [];
    const searchLower = approverSearchTerm.toLowerCase();
    return userStore.approvers.filter((approver) => {
      const text = approver.text || "";
      return text.toLowerCase().includes(searchLower);
    });
  }, [userStore.approvers, approverSearchTerm]);

  // Store original values when userLeaveSettings are loaded
  useEffect(() => {
    const userLeaveSettings = getUserInfo?.userLeaveSettings || userStore.users?.userLeaveSettings || [];
    if (userLeaveSettings.length > 0) {
      userLeaveSettings.forEach((setting) => {
        const leaveTypeId = setting.leaveTypeId || setting.id;
        if (leaveTypeId && setting.value !== undefined && setting.value !== null && setting.value !== 0) {
          // Only store non-zero values as original values
          originalValuesRef.current[leaveTypeId] = setting.value;
        }
      });
    }
  }, [getUserInfo?.userLeaveSettings, userStore.users?.userLeaveSettings]);

  const formDataReady = !userStore.userDataLoading && !userStore.isLoading && !workplacesLoading;
  const noWorkplaces = !hasWorkplaces;

  useEffect(() => {
    if (formDataReady && noWorkplaces) {
      showWorkplaceRequired();
    }
  }, [formDataReady, noWorkplaces, showWorkplaceRequired]);

  if (formDataReady && noWorkplaces) {
    return <div className="min-vh-100 bg-light">{workplaceRequiredModal}</div>;
  }

  return (
    <Container className="py-4 px-3 px-md-4" fluid>
      <div className="min-vh-100 bg-light">
        {/* Header — CommonPageHeader (shared animation) */}
        <Row className="mb-2">
          <Col>
            <CommonPageHeader
              barClassName="office-header-bar"
              contentClassName="office-header-content"
              leftClassName="office-header-left"
              iconWrapperClassName="office-header-icon-wrapper"
              textClassName="office-header-text"
              titleClassName="office-page-title"
              descriptionClassName="office-page-description"
              icon={
                <i
                  className={`bi ${mode === "edit" ? "bi-person-check-fill" : "bi-person-plus-fill"}`}
                  style={{ fontSize: "20px" }}
                />
              }
              title={mode === "edit" ? t("app.users.editUserTitle") : t("app.users.addNewUserTitle")}
              description={
                mode === "edit" ? t("app.users.editUserDescription") : t("app.users.addNewUserDescription")
              }
              onBack={() => props.history.push("/users")}
              backLabel={t("app.common.back")}
            />
          </Col>
        </Row>

        {/* Form Container */}
        <Dimmer.Dimmable as={Segment} dimmed={userStore.userDataLoading || userStore.isLoading || leaveTypesLoading || formLoading}>
          <Dimmer active={userStore.userDataLoading || userStore.isLoading || leaveTypesLoading || formLoading} inverted={true}>
            <FetchingSpinner
              text={
                formLoading
                  ? (mode === "edit" ? t("app.users.updatingUser") : t("app.users.creatingUser"))
                  : userStore.userDataLoading
                    ? (mode === "edit" ? t("app.users.loadingUserData") : t("app.users.initializingForm"))
                    : userStore.isLoading
                      ? t("app.users.loadingFormOptions")
                      : leaveTypesLoading
                        ? t("app.users.loadingLeaveTypes")
                        : ""
              }
            />
          </Dimmer>

          <div className="container-fluid py-3 px-3 user-form-page-wrap">
            <div className="row justify-content-center">
              <div className="col-12 user-form-with-guide">
                <Formik
                  enableReinitialize={true}
                  key={`${mode}-${userStore.users?.id || "new"}`}
                  initialValues={{
                    name: getUserInfo?.name || "",
                    password: getUserInfo?.password || "",
                    email: getUserInfo?.email || "",
                    empStartDate: mode === "create"
                      ? (getUserInfo?.empStartDate || new Date().toISOString().split('T')[0])
                      : (getUserInfo?.empStartDate || ""),
                    role: getUserInfo?.role || null,
                    officeId: getUserInfo?.officeId || null,
                    departmentId: getUserInfo?.departmentId || null,
                    designationId:
                      getUserInfo?.designationId ??
                      getUserInfo?.DesignationId ??
                      "",
                    employmentTypeId:
                      getUserInfo?.employmentTypeId ??
                      getUserInfo?.EmploymentTypeId ??
                      "",
                    teamIds: resolveTeamIdsFromUser(
                      (userStore.users && !Array.isArray(userStore.users) && userStore.users.id)
                        ? userStore.users
                        : (getUserInfo ?? userStore.users)
                    ),
                    // Prefer Redux user when loaded so dependents survive Formik remount
                    // (key changes on user id before setUserInfo runs).
                    additionalProfile: resolveAdditionalProfileFromUser(
                      (userStore.users && !Array.isArray(userStore.users) && userStore.users.id)
                        ? userStore.users
                        : (getUserInfo ?? userStore.users),
                      officeStore.countries
                    ),
                    userLeaveSettings: getUserInfo?.userLeaveSettings || [],
                    leaveYearStart: mode === "create"
                      ? (getUserInfo?.leaveYearStart || new Date().getMonth() + 1)
                      : (getUserInfo?.leaveYearStart || null),
                    approvalId: getUserInfo?.approvalId || "",
                    isActive: getUserInfo?.isActive !== false,
                    isLoginDisabled: getUserInfo?.isLoginDisabled !== undefined ? getUserInfo.isLoginDisabled : (userStore.users?.isLoginDisabled !== undefined ? userStore.users.isLoginDisabled : false),
                    isFowardLoginDetails: getUserInfo?.isFowardLoginDetails !== undefined ? getUserInfo.isFowardLoginDetails : true,
                    imageUrl:
                      getUserInfo?.imageUrl || userStore.users?.imageUrl || "", // Add image URL field with fallback
                  }}
                  validateOnChange={true}
                  validateOnBlur={true}
                  onSubmit={(values) => onSubmitBtnClick(values)}
                  validationSchema={userFormValidationSchema}
                >
                  {({
                    values,
                    setFieldValue,
                    setFieldTouched,
                    setFieldError,
                    handleSubmit,
                    errors,
                    touched,
                    validateField,
                    submitCount,
                  }) => {
                    // Store setFieldValue in ref for external access
                    setFieldValueRef.current = setFieldValue;

                    const isAdminInEditMode = values.role === 2;

                    const additionalField = (fieldId) =>
                      additionalProfileFieldProps(fieldId, errors, touched, setFieldTouched, submitCount);

                    const designationOptions = (() => {
                      const list = designationList || [];
                      const sel = values.designationId;
                      if (mode === "create") {
                        return list.filter((x) => x.isActive !== false);
                      }
                      return list.filter(
                        (x) =>
                          x.isActive !== false ||
                          (sel && String(x.id) === String(sel))
                      );
                    })();
                    const employmentTypeOptions = (() => {
                      const list = employmentTypeList || [];
                      const sel = values.employmentTypeId;
                      if (mode === "create") {
                        return list.filter((x) => x.isActive !== false);
                      }
                      return list.filter(
                        (x) =>
                          x.isActive !== false ||
                          (sel && String(x.id) === String(sel))
                      );
                    })();
                    const teamOptions = (() => {
                      const list = teamList || [];
                      const selectedIds = values.teamIds || [];
                      if (mode === "create") {
                        return list.filter((x) => x.isActive !== false);
                      }
                      return list.filter(
                        (x) =>
                          x.isActive !== false ||
                          selectedIds.some((teamId) => String(x.id) === String(teamId))
                      );
                    })();

                    const designationSelectOptions = designationOptions.map((row) => ({
                      value: String(row.id),
                      label: row.name,
                    }));
                    const employmentTypeSelectOptions = employmentTypeOptions.map((row) => ({
                      value: String(row.id),
                      label: row.name,
                    }));
                    const teamSelectOptions = teamOptions.map((row) => ({
                      value: String(row.id),
                      label: row.name,
                    }));

                    // Get selected approver display text
                    const selectedApproverText = (() => {
                      if (!values.approvalId) return "Select approver";
                      const approver = userStore.approvers?.find((a) => a.value === values.approvalId);
                      if (!approver) return "Select approver";
                      // Parse name and email from text (format: "Name email@example.com")
                      const parts = approver.text.split(" ");
                      if (parts.length >= 2) {
                        const email = parts[parts.length - 1];
                        const name = parts.slice(0, -1).join(" ");
                        return `${name} (${email})`;
                      }
                      return approver.text;
                    })();

                    // Calculate prorated leave information based on leave year start
                    // Include the leave year start month in the count (months from start month through December)
                    // Example: If January is selected, months = Jan..Dec = 12 months (100%)
                    // Example: If July is selected, months = July, Aug, Sep, Oct, Nov, Dec = 6 months (50%)
                    const getProratedLeaveInfo = () => {
                      if (!values.leaveYearStart) return null;

                      // Ensure leaveYearStart is a number
                      const leaveYearStartMonth = Number(values.leaveYearStart);
                      if (isNaN(leaveYearStartMonth) || leaveYearStartMonth < 1 || leaveYearStartMonth > 12) {
                        return null;
                      }

                      const selectedMonth = months.find((m) => m.value === leaveYearStartMonth);
                      if (!selectedMonth) return null;

                      // For edit mode, use employment start date if available, otherwise use current date
                      // For create mode, use current date (user is being registered now)
                      let referenceDate = new Date();
                      if (mode === "edit" && values.empStartDate) {
                        referenceDate = new Date(values.empStartDate);
                      }

                      // Months in the leave year = leave year start month through December (inclusive)
                      // Formula: 13 - leaveYearStartMonth (e.g. Jan=12, Jul=6, Dec=1)
                      let monthsRemaining = 13 - leaveYearStartMonth;

                      // Ensure monthsRemaining is at least 1 and at most 12
                      if (monthsRemaining <= 0) {
                        monthsRemaining = 1;
                      }
                      monthsRemaining = Math.max(1, Math.min(12, monthsRemaining));

                      // Calculate proration factor: months in leave year / 12 (full leave year)
                      const prorationFactor = monthsRemaining / 12;

                      return {
                        selectedMonth: selectedMonth.text,
                        monthsRemaining,
                        prorationFactor
                      };
                    };

                    const proratedInfo = getProratedLeaveInfo();

                    return (
                      <>
                        <UserLeaveSettingsInitializer
                          mode={mode}
                          allLeaveTypes={userStore.users?.allLeaveTypes || []}
                          userSettings={userStore.users?.userLeaveSettings || []}
                          initializedRef={initializedRef}
                          setFieldValue={setFieldValue}
                          dispatch={dispatch}
                          userStore={userStore}
                          customAccrualSettings={customAccrualSettings}
                          setCustomAccrualSettings={setCustomAccrualSettings}
                          setCustomAccrualRateUnits={setCustomAccrualRateUnits}
                          setCustomAccrualCapUnits={setCustomAccrualCapUnits}
                        />
                        <UserFormValidationGuide
                          values={values}
                          errors={errors}
                          touched={touched}
                          submitCount={submitCount}
                          setFieldTouched={setFieldTouched}
                          mode={mode}
                          isAdminInEditMode={isAdminInEditMode}
                          formReady={
                            formDataReady &&
                            !leaveTypesLoading &&
                            !formLoading
                          }
                        />
                        <form onSubmit={handleSubmit}>
                          {mode === "create" && isSuspended && (
                            <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAddNewUsers" />
                          )}
                          {/* Personal Information Section */}
                          <div
                            style={{ transform: "none" }}
                            className="card mb-3 border-0 shadow-sm w-100"
                          >
                            <div className="card-header user-form-card-header">
                              <div className="px-5">
                                <div className="d-flex align-items-center mb-2" style={{ gap: '12px', flexWrap: 'wrap' }}>
                                  <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-person-fill me-2 text-white"></i>
                                    Personal Information
                                  </h5>
                                </div>
                                <p className="mb-0 mt-2 text-light small">
                                  Basic user details and contact information
                                </p>
                              </div>
                            </div>
                            <div className="card-body p-4 w-100">
                              {/* Profile Image - First Row */}
                              <div className="row mb-2">
                                <div className="col-12 d-flex justify-content-center">
                                  <div className="d-flex flex-column align-items-center">
                                    <label className="form-label fw-semibold mb-3">
                                      Profile Image
                                    </label>
                                    <div className="position-relative mb-3">
                                      <div
                                        className="rounded-circle bg-light border d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "120px",
                                          height: "120px",
                                        }}
                                      >
                                        {(() => {
                                          const imageUrl =
                                            values.imageUrl ||
                                            userStore.users?.imageURL;

                                          return imageUrl ? (
                                            <img
                                              src={imageUrl}
                                              alt="Profile"
                                              className="rounded-circle"
                                              style={{
                                                width: "120px",
                                                height: "120px",
                                                objectFit: "cover",
                                              }}
                                              onError={(e) => {
                                                if (typeof posthog !== "undefined" && posthog.capture) {
                                                  posthog.capture("userFormImageLoad", {
                                                    type: "exception",
                                                    success: false,
                                                    reason: "Image failed to load",
                                                    imageUrl,
                                                  });
                                                }
                                                e.target.style.display = "none";
                                              }}
                                              onLoad={() => {
                                                console.log(
                                                  "Image loaded successfully:",
                                                  imageUrl
                                                );
                                              }}
                                            />
                                          ) : (
                                            <i
                                              className="bi bi-person-fill text-muted"
                                              style={{ fontSize: "3rem" }}
                                            ></i>
                                          );
                                        })()}
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm p-0 position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: "30px", height: "32px" }}
                                        onClick={() =>
                                          profileImageInputRef.current.click()
                                        }
                                        disabled={
                                          isUploadingImage || isOptimizing
                                        }
                                      >
                                        {isUploadingImage || isOptimizing ? (
                                          <div
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                          >
                                            <span className="visually-hidden">
                                              Loading...
                                            </span>
                                          </div>
                                        ) : (
                                          <i
                                            className="bi bi-pencil-fill"
                                            style={{ fontSize: "0.75rem" }}
                                          ></i>
                                        )}
                                      </button>
                                    </div>
                                    <input
                                      ref={profileImageInputRef}
                                      type="file"
                                      accept="image/*"
                                      className="d-none"
                                      onChange={(e) =>
                                        handleProfileImageChange(e, setFieldValue)
                                      }
                                    />
                                    <small className="text-muted text-center">
                                      {isUploadingImage || isOptimizing ? (
                                        <div className="d-flex flex-column align-items-center">
                                          <div
                                            className="progress w-100 mb-2"
                                            style={{ height: "4px" }}
                                          >
                                            <div
                                              className="progress-bar"
                                              style={{
                                                width: `${isOptimizing
                                                  ? optimizationProgress
                                                  : imageUploadProgress
                                                  }%`,
                                              }}
                                            ></div>
                                          </div>
                                          <span>
                                            {isOptimizing
                                              ? "Optimizing image..."
                                              : "Uploading image..."}
                                          </span>
                                        </div>
                                      ) : (
                                        "Click the pen icon to upload a profile image"
                                      )}
                                    </small>
                                  </div>
                                </div>
                              </div>

                              {/* Form Fields - Second Row */}
                              <div className="row">
                                {/* Full Name */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="fullName"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Full Name{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    {errors.name && touched.name && (
                                      <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                        {errors.name}
                                      </small>
                                    )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-person-fill"></i>
                                    </span>
                                    <input
                                      type="text"
                                      className={`form-control ${errors.name && touched.name
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="fullName"
                                      placeholder={t("app.users.enterFullName")}
                                      value={values.name}
                                      onChange={(e) =>
                                        setFieldValue("name", e.target.value)
                                      }
                                      onBlur={() => setFieldTouched("name", true)}
                                    />
                                  </div>
                                </div>

                                {/* Email Address */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="email"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Email Address{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    {errors.email && touched.email && (
                                      <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                        {errors.email}
                                      </small>
                                    )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-envelope-fill"></i>
                                    </span>
                                    <input
                                      type="email"
                                      className={`form-control ${errors.email && touched.email
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="email"
                                      placeholder={t("app.users.enterEmailAddress")}
                                      value={values.email}
                                      onChange={(e) =>
                                        setFieldValue("email", e.target.value)
                                      }
                                      onBlur={() =>
                                        setFieldTouched("email", true)
                                      }
                                      disabled={mode === "edit"}
                                    />
                                  </div>
                                </div>

                                {/* Password (only in create mode) */}
                                {mode !== "edit" && (
                                  <div className="col-12 col-md-6 mt-3">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <label
                                        htmlFor="password"
                                        className="form-label fw-semibold mb-0"
                                      >
                                        Password{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      {errors.password && touched.password && (
                                        <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                          {errors.password}
                                        </small>
                                      )}
                                    </div>
                                    <div className="input-group d-flex align-items-stretch">
                                      <span
                                        className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                        style={{
                                          fontSize: "0.875rem",
                                          padding: "0",
                                          minWidth: "auto",
                                          width: "40px",
                                          borderRight: "none",
                                        }}
                                      >
                                        <i className="bi bi-lock-fill"></i>
                                      </span>
                                      <input
                                        type={showPassword ? "text" : "password"}
                                        className={`form-control ${errors.password && touched.password
                                          ? "is-invalid"
                                          : ""
                                          }`}
                                        style={{
                                          padding: "0.375rem 0.75rem",
                                          minHeight: "38px",
                                        }}
                                        id="password"
                                        placeholder={t("app.users.enterPassword")}
                                        value={values.password}
                                        onChange={async (e) => {
                                          setFieldValue("password", e.target.value);
                                          setFieldTouched("password", true);
                                          await validateField("password");
                                        }}
                                        onBlur={() =>
                                          setFieldTouched("password", true)
                                        }
                                        autoComplete="new-password"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                          borderLeft: "none",
                                          fontSize: "0.875rem",
                                          padding: "0.375rem 0.75rem",
                                          minWidth: "45px",
                                        }}
                                        title={showPassword ? "Hide password" : "Show password"}
                                      >
                                        <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={async () => {
                                          const newPassword = generateRandomPassword();
                                          // Clear error first to avoid blink, then set value without triggering validation
                                          setFieldError("password", undefined);
                                          setFieldValue("password", newPassword, false);
                                          setFieldTouched("password", true, false);
                                          await new Promise(resolve => setTimeout(resolve, 0));
                                          const validationError = await validateField("password");
                                          if (validationError) {
                                            setFieldError("password", validationError);
                                          }
                                        }}
                                        style={{
                                          borderLeft: "none",
                                          fontSize: "0.875rem",
                                          padding: "0.375rem 0.75rem",
                                          whiteSpace: "nowrap",
                                        }}
                                        title="Generate a secure random password"
                                      >
                                        <i className="bi bi-shuffle me-1"></i>
                                        Generate
                                      </button>
                                    </div>
                                    <small className="form-text text-muted d-block mt-2">
                                      Set a temporary password for the user. They can <b>reset</b> their password after logging in.
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Employment Details Section */}
                          <div
                            style={{ transform: "none" }}
                            className="card mb-3 border-0 shadow-sm w-100"
                          >
                            <div className="card-header user-form-card-header">
                              <div className="px-5">
                                <h5 className="mb-0 fw-bold">
                                  <i className="bi bi-briefcase-fill me-2 text-white"></i>
                                  Employment Details
                                </h5>
                                <p className="mb-0 mt-2 text-light small">
                                  Work-related information and role assignment
                                </p>
                              </div>
                            </div>
                            <div className="card-body p-4">
                              <div className="row g-3">
                                {/* Assign Role */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="assignRole"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Assign Role{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    {errors.role && touched.role && (
                                      <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                        {errors.role}
                                      </small>
                                    )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-shield-fill-check"></i>
                                    </span>
                                    <select
                                      className={`form-select flex-grow-1 ${errors.role && touched.role
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="assignRole"
                                      value={values.role}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "role",
                                          Number(e.target.value)
                                        )
                                      }
                                      onBlur={() => setFieldTouched("role", true)}
                                      disabled={userStore.isLoading || userStore.userDataLoading}
                                    >
                                      <option value="">Select role</option>
                                      {userStore.adminRoles.map((role) => (
                                        <option
                                          key={role.value}
                                          value={role.value}
                                        >
                                          {role.text}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                {/* Workplace */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="workplace"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Workplace{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    {errors.officeId && touched.officeId && (
                                      <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                        {errors.officeId}
                                      </small>
                                    )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-building-fill"></i>
                                    </span>
                                    <select
                                      className={`form-select flex-grow-1 ${errors.officeId && touched.officeId
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="workplace"
                                      value={values.officeId}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "officeId",
                                          Number(e.target.value)
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("officeId", true)
                                      }
                                      disabled={userStore.isLoading || userStore.userDataLoading}
                                    >
                                      <option value="">Select Workplace</option>
                                      {userStore.offices.map((office) => (
                                        <option
                                          key={office.value}
                                          value={office.value}
                                        >
                                          {office.text}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                {/* Department */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="department"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Department{" "}
                                      {/*   <span className="text-danger">*</span> */}
                                    </label>
                                    {errors.departmentId &&
                                      touched.departmentId && (
                                        <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                          {errors.departmentId}
                                        </small>
                                      )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-diagram-3-fill"></i>
                                    </span>
                                    <select
                                      className={`form-select flex-grow-1 ${errors.departmentId &&
                                        touched.departmentId
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="department"
                                      value={values.departmentId}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "departmentId",
                                          Number(e.target.value)
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("departmentId", true)
                                      }
                                      disabled={userStore.isLoading || userStore.userDataLoading}
                                    >
                                      <option value="">Select Department</option>
                                      {(() => {
                                        // Handle departments data structure - it might be directly an array or have lookups
                                        let departments = [];
                                        if (userStore.departments) {
                                          if (
                                            Array.isArray(userStore.departments)
                                          ) {
                                            // If it's directly an array
                                            departments = userStore.departments;
                                          } else if (
                                            userStore.departments.lookups &&
                                            Array.isArray(
                                              userStore.departments.lookups
                                            )
                                          ) {
                                            // If it has lookups property
                                            departments =
                                              userStore.departments.lookups;
                                          } else if (
                                            userStore.departments[0] &&
                                            Array.isArray(
                                              userStore.departments[0]
                                            )
                                          ) {
                                            // If it's nested array structure
                                            departments =
                                              userStore.departments[0];
                                          }
                                        }

                                        // Filter departments: in create mode, show only active departments
                                        if (mode === "create") {
                                          departments = departments.filter(
                                            (dept) => dept.isActive !== false
                                          );
                                        }

                                        if (
                                          !Array.isArray(departments) ||
                                          departments.length === 0
                                        ) {
                                          return (
                                            <option value="" disabled>
                                              No departments available
                                            </option>
                                          );
                                        }

                                        return departments.map((department) => (
                                          <option
                                            key={
                                              department.value || department.id
                                            }
                                            value={
                                              department.value || department.id
                                            }
                                          >
                                            {department.text || department.name}
                                          </option>
                                        ));
                                      })()}
                                    </select>
                                  </div>
                                </div>

                                {/* Employment Start Date */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label
                                      htmlFor="empStartDate"
                                      className="form-label fw-semibold mb-0"
                                    >
                                      Employment Start Date{" "}
                                      {!isAdminInEditMode && (
                                        <span className="text-danger">*</span>
                                      )}
                                    </label>
                                    {errors.empStartDate &&
                                      touched.empStartDate && (
                                        <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                          {errors.empStartDate}
                                        </small>
                                      )}
                                  </div>
                                  <div className="d-flex align-items-stretch">
                                    <span
                                      className="bg-light border border-end-0 d-flex align-items-center px-3"
                                      style={{
                                        fontSize: "0.875rem",
                                        minWidth: "auto",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <i className="bi bi-calendar-event-fill"></i>
                                    </span>
                                    <input
                                      type="date"
                                      className={`form-control rounded-0 ${errors.empStartDate &&
                                        touched.empStartDate
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{ flex: "1 1 auto" }}
                                      id="empStartDate"
                                      value={values.empStartDate}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "empStartDate",
                                          e.target.value
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("empStartDate", true)
                                      }
                                    />
                                  </div>
                                </div>

                                {/* Leave & Timesheet Approver, designation, employment type, teams */}
                                <div className="col-12">
                                  <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                          <label
                                            htmlFor="leaveApprover"
                                            className="form-label fw-semibold mb-0"
                                          >
                                            {t("app.users.approverManagerLabel")}
                                            {!isAdminInEditMode && (
                                              <span className="text-danger">*</span>
                                            )}
                                          </label>
                                          <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            style={{
                                              border: "none",
                                              background: "none",
                                              color: "#0dcaf0",
                                              fontSize: "1rem",
                                              lineHeight: 1,
                                              textDecoration: "none",
                                            }}
                                            onClick={() => setShowApproverManagerInfoPopup(true)}
                                            title={t("app.users.approverManagerInfoTitle")}
                                            aria-label={t("app.users.approverManagerInfoTitle")}
                                          >
                                            <i className="bi bi-info-circle-fill"></i>
                                          </button>
                                        </div>
                                        {errors.approvalId && touched.approvalId && (
                                          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                            {errors.approvalId}
                                          </small>
                                        )}
                                      </div>
                                      <div
                                        ref={approverDropdownRef}
                                        className="position-relative"
                                      >
                                    <div className="input-group d-flex align-items-stretch">
                                      <span
                                        className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                        style={{
                                          fontSize: "0.875rem",
                                          padding: "0",
                                          minWidth: "auto",
                                          width: "40px",
                                          borderRight: "none",
                                        }}
                                      >
                                        <i className="bi bi-person-check-fill"></i>
                                      </span>
                                      <div
                                        id="leaveApprover"
                                        role="button"
                                        tabIndex={0}
                                        className={`form-control d-flex align-items-center justify-content-between ${errors.approvalId && touched.approvalId
                                          ? "is-invalid"
                                          : ""
                                          }`}
                                        style={{
                                          cursor: "pointer",
                                          minHeight: "38px",
                                          backgroundColor: "#fff",
                                          padding: "0.375rem 0.75rem",
                                          fontSize: "1rem",
                                        }}
                                        ref={approverInputRef}
                                        onClick={() => {
                                          if (!userStore.isLoading && !userStore.userDataLoading) {
                                            if (!isApproverDropdownOpen && approverInputRef.current) {
                                              // Calculate position for fixed dropdown
                                              const rect = approverInputRef.current.getBoundingClientRect();
                                              setDropdownPosition({
                                                top: rect.bottom + window.scrollY + 2,
                                                left: rect.left + window.scrollX,
                                                width: rect.width
                                              });
                                            }
                                            setIsApproverDropdownOpen(!isApproverDropdownOpen);
                                            if (!isApproverDropdownOpen) {
                                              setApproverSearchTerm("");
                                            }
                                          }
                                        }}
                                        onBlur={() => {
                                          setTimeout(() => {
                                            setFieldTouched("approvalId", true);
                                          }, 200);
                                        }}
                                      >
                                        <span
                                          className={`${!values.approvalId ? "text-muted" : ""}`}
                                          style={{
                                            fontSize: "inherit",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {selectedApproverText}
                                        </span>
                                        <i
                                          className={`bi ${isApproverDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"} text-muted`}
                                          style={{ fontSize: "0.75rem" }}
                                        ></i>
                                      </div>
                                    </div>

                                    {/* Dropdown Menu */}
                                    {isApproverDropdownOpen && (
                                      <div
                                        className="approver-dropdown-menu position-fixed bg-white border rounded shadow-lg"
                                        style={{
                                          zIndex: 9999,
                                          top: `${dropdownPosition.top}px`,
                                          left: `${dropdownPosition.left}px`,
                                          width: `${dropdownPosition.width}px`,
                                          maxHeight: "300px",
                                          display: "flex",
                                          flexDirection: "column",
                                          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                                        }}
                                      >
                                        {/* Search Input */}
                                        <div className="p-2 border-bottom">
                                          <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-light">
                                              <i className="bi bi-search"></i>
                                            </span>
                                            <input
                                              type="text"
                                              className="form-control"
                                              placeholder={t("app.users.searchApproverPlaceholder")}
                                              value={approverSearchTerm}
                                              onChange={(e) => {
                                                setApproverSearchTerm(e.target.value);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              autoFocus
                                            />
                                          </div>
                                        </div>

                                        {/* Scrollable List */}
                                        <div
                                          className="overflow-auto"
                                          style={{ maxHeight: "250px" }}
                                        >
                                          {filteredApprovers.length > 0 ? (
                                            filteredApprovers.map((approver) => {
                                              // Parse name and email from text
                                              const parts = approver.text.split(" ");
                                              const email = parts.length >= 2 ? parts[parts.length - 1] : "";
                                              const name = parts.length >= 2 ? parts.slice(0, -1).join(" ") : approver.text;

                                              return (
                                                <div
                                                  key={approver.value}
                                                  className={`px-3 py-2 d-flex flex-column ${values.approvalId === approver.value
                                                    ? "bg-primary bg-opacity-10"
                                                    : "hover-bg-light"
                                                    }`}
                                                  style={{
                                                    cursor: "pointer",
                                                    borderBottom: "1px solid #f0f0f0",
                                                    transition: "background-color 0.15s ease",
                                                  }}
                                                  onClick={() => {
                                                    setFieldValue("approvalId", approver.value);
                                                    setIsApproverDropdownOpen(false);
                                                    setApproverSearchTerm("");
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (values.approvalId !== approver.value) {
                                                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (values.approvalId !== approver.value) {
                                                      e.currentTarget.style.backgroundColor = "transparent";
                                                    }
                                                  }}
                                                >
                                                  <span
                                                    className="fw-semibold"
                                                    style={{ fontSize: "0.875rem", color: "#212529" }}
                                                  >
                                                    {name}
                                                  </span>
                                                  <span
                                                    className="text-muted"
                                                    style={{ fontSize: "0.75rem" }}
                                                  >
                                                    {email}
                                                  </span>
                                                </div>
                                              );
                                            })
                                          ) : (
                                            <div className="px-3 py-3 text-center text-muted">
                                              <small>No approvers found</small>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <label htmlFor="designationId" className="form-label fw-semibold mb-0">
                                          {t("app.users.designation")}
                                          <span className="text-muted fw-normal ms-1 small">({t("app.users.optionalField")})</span>
                                        </label>
                                        {errors.designationId && touched.designationId && (
                                          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                            {errors.designationId}
                                          </small>
                                        )}
                                      </div>
                                      <div
                                        className={
                                          errors.designationId && touched.designationId
                                            ? "user-form-rs-invalid-wrap"
                                            : ""
                                        }
                                      >
                                        <UserFormIconSelect
                                          icon="bi-briefcase-fill"
                                          invalid={errors.designationId && touched.designationId}
                                        >
                                          <Select
                                            inputId="designationId"
                                            instanceId="user-form-designation"
                                            isClearable
                                            isSearchable
                                            placeholder={t("app.users.selectDesignation")}
                                            options={designationSelectOptions}
                                            value={
                                              designationSelectOptions.find(
                                                (o) => o.value === String(values.designationId || "")
                                              ) || null
                                            }
                                            onChange={(opt) =>
                                              setFieldValue("designationId", opt ? opt.value : "")
                                            }
                                            onBlur={() => setFieldTouched("designationId", true)}
                                            isDisabled={userStore.isLoading || userStore.userDataLoading}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            styles={USER_FORM_RS_STYLES}
                                            classNamePrefix="user-form-rs"
                                          />
                                        </UserFormIconSelect>
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <label htmlFor="employmentTypeId" className="form-label fw-semibold mb-0">
                                          {t("app.users.employmentType")}
                                          <span className="text-muted fw-normal ms-1 small">({t("app.users.optionalField")})</span>
                                        </label>
                                        {errors.employmentTypeId && touched.employmentTypeId && (
                                          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                            {errors.employmentTypeId}
                                          </small>
                                        )}
                                      </div>
                                      <div
                                        className={
                                          errors.employmentTypeId && touched.employmentTypeId
                                            ? "user-form-rs-invalid-wrap"
                                            : ""
                                        }
                                      >
                                        <UserFormIconSelect
                                          icon="bi-person-badge-fill"
                                          invalid={errors.employmentTypeId && touched.employmentTypeId}
                                        >
                                          <Select
                                            inputId="employmentTypeId"
                                            instanceId="user-form-employment-type"
                                            isClearable
                                            isSearchable
                                            placeholder={t("app.users.selectEmploymentType")}
                                            options={employmentTypeSelectOptions}
                                            value={
                                              employmentTypeSelectOptions.find(
                                                (o) => o.value === String(values.employmentTypeId || "")
                                              ) || null
                                            }
                                            onChange={(opt) =>
                                              setFieldValue("employmentTypeId", opt ? opt.value : "")
                                            }
                                            onBlur={() => setFieldTouched("employmentTypeId", true)}
                                            isDisabled={userStore.isLoading || userStore.userDataLoading}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            styles={USER_FORM_RS_STYLES}
                                            classNamePrefix="user-form-rs"
                                          />
                                        </UserFormIconSelect>
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <label htmlFor="teamIds" className="form-label fw-semibold mb-0">
                                          {t("app.sidebar.organizationTeams")}
                                          <span className="text-muted fw-normal ms-1 small">({t("app.users.optionalField")})</span>
                                        </label>
                                        {errors.teamIds && touched.teamIds && (
                                          <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                            {errors.teamIds}
                                          </small>
                                        )}
                                      </div>
                                      <div
                                        className={
                                          errors.teamIds && touched.teamIds
                                            ? "user-form-rs-invalid-wrap"
                                            : ""
                                        }
                                      >
                                        <UserFormIconSelect
                                          icon="bi-people-fill"
                                          invalid={errors.teamIds && touched.teamIds}
                                        >
                                          <Select
                                            inputId="teamIds"
                                            instanceId="user-form-teams"
                                            isMulti
                                            isClearable
                                            isSearchable
                                            placeholder={t("app.users.selectTeams")}
                                            options={teamSelectOptions}
                                            value={teamSelectOptions.filter((o) =>
                                              (values.teamIds || []).some((id) => String(id) === o.value)
                                            )}
                                            onChange={(opts) =>
                                              setFieldValue(
                                                "teamIds",
                                                Array.isArray(opts) ? opts.map((o) => o.value) : []
                                              )
                                            }
                                            onBlur={() => setFieldTouched("teamIds", true)}
                                            isDisabled={userStore.isLoading || userStore.userDataLoading}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            styles={USER_FORM_RS_STYLES}
                                            classNamePrefix="user-form-rs"
                                          />
                                        </UserFormIconSelect>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Leave Year Start */}
                                <div className="col-12 col-md-6">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                      <label
                                        htmlFor="leaveYearStarts"
                                        className="form-label fw-semibold mb-0"
                                      >
                                        Leave Year Start{" "}
                                        {!isAdminInEditMode && (
                                          <span className="text-danger">*</span>
                                        )}
                                      </label>
                                      {values.leaveYearStart && values.leaveYearStart !== null && values.leaveYearStart !== undefined && values.leaveYearStart !== "" && Number(values.leaveYearStart) >= 1 && Number(values.leaveYearStart) <= 12 && (
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 ms-2"
                                          style={{
                                            padding: "0",
                                            border: "none",
                                            background: "none",
                                            color: "#0dcaf0",
                                            fontSize: "1rem",
                                            lineHeight: "1",
                                            textDecoration: "none",
                                          }}
                                          onClick={() => setShowLeaveYearInfoPopup(true)}
                                          title="View prorated leave calculation info"
                                        >
                                          <i className="bi bi-info-circle-fill"></i>
                                        </button>
                                      )}
                                    </div>
                                    {errors.leaveYearStart &&
                                      touched.leaveYearStart && (
                                        <small className="text-danger" style={{ fontSize: "0.875rem" }}>
                                          {errors.leaveYearStart}
                                        </small>
                                      )}
                                  </div>
                                  <div className="input-group d-flex align-items-stretch">
                                    <span
                                      className="input-group-text bg-light d-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: "0.875rem",
                                        padding: "0",
                                        minWidth: "auto",
                                        width: "40px",
                                        borderRight: "none",
                                      }}
                                    >
                                      <i className="bi bi-calendar-range-fill"></i>
                                    </span>
                                    <select
                                      className={`form-select flex-grow-1 ${errors.leaveYearStart &&
                                        touched.leaveYearStart
                                        ? "is-invalid"
                                        : ""
                                        }`}
                                      style={{
                                        padding: "0.375rem 0.75rem",
                                        minHeight: "38px",
                                      }}
                                      id="leaveYearStarts"
                                      value={values.leaveYearStart || ""}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "leaveYearStart",
                                          e.target.value === "" ? null : Number(e.target.value)
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("leaveYearStart", true)
                                      }
                                      disabled={userStore.isLoading || userStore.userDataLoading}
                                    >
                                      <option value="">Select month</option>
                                      {months.map((month) => (
                                        <option
                                          key={month.value}
                                          value={month.value}
                                        >
                                          {month.text}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </div>

                          {/* Additional Information Section */}
                          <div
                            style={{ transform: "none" }}
                            className="card mb-3 border-0 shadow-sm w-100"
                          >
                            <div className="card-header user-form-card-header">
                              <div className="px-5">
                                <h5 className="mb-0 fw-bold">
                                  <i className="bi bi-person-vcard-fill me-2 text-white"></i>
                                  {t("app.users.additionalInformationTitle")}
                                </h5>
                                <p className="mb-0 mt-2 text-light small">
                                  {t("app.users.additionalInformationDescription")}
                                </p>
                              </div>
                            </div>
                            <div className="card-body p-4">
                              <UserFormCollapsibleSubsection
                                id={ADDITIONAL_PROFILE_SECTIONS.personal}
                                icon="bi-person-fill"
                                title={t("app.users.personalDetailsSubsection")}
                                expanded={expandedAdditionalSections.personal}
                                onToggle={() => toggleAdditionalSection(ADDITIONAL_PROFILE_SECTIONS.personal)}
                              >
                              <div className="row g-3 mb-2">
                                <UserFormEnrollField
                                  id="employeeCode"
                                  label={t("app.users.employeeCode", "Employee Code / Enroll No.")}
                                  icon="bi-hash"
                                  optional
                                  maxLength={20}
                                  placeholder={t("app.attendance.machines.enrollSelectPlaceholder")}
                                  hint={t("app.users.employeeCodeAttendanceHint")}
                                  enrollOptions={enrollOptions}
                                  {...additionalField('employeeCode')}
                                  value={values.additionalProfile?.employeeCode}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      employeeCode: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormNativeSelectField
                                  id="gender"
                                  label={t("app.users.gender")}
                                  icon="bi-gender-ambiguous"
                                  optional
                                  {...additionalField('gender')}
                                  value={values.additionalProfile?.gender}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      gender: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                  placeholder={t("app.users.selectGender")}
                                >
                                  {getUserFormGenderOptions(t).map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </UserFormNativeSelectField>
                                <UserFormTextField
                                  id="dob"
                                  label={t("app.users.dateOfBirth")}
                                  icon="bi-calendar-heart-fill"
                                  optional
                                  placeholder={t("app.users.selectDateOfBirth")}
                                  {...additionalField('dob')}
                                  type="date"
                                  max={getLocalDateInputMax()}
                                  value={values.additionalProfile?.dob}
                                  onChange={(e) => {
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      dob: e.target.value,
                                    });
                                    setFieldTouched("additionalProfile.dob", true, false);
                                  }}
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormNativeSelectField
                                  id="maritalStatus"
                                  label={t("app.users.maritalStatus")}
                                  icon="bi-heart-fill"
                                  optional
                                  {...additionalField('maritalStatus')}
                                  value={values.additionalProfile?.maritalStatus}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      maritalStatus: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                  placeholder={t("app.users.selectMaritalStatus")}
                                >
                                  {MeritalStatusOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.text}
                                    </option>
                                  ))}
                                </UserFormNativeSelectField>
                                <UserFormTextField
                                  id="fatherOrHusbandName"
                                  label={t("app.users.fatherOrHusbandName")}
                                  icon="bi-person-lines-fill"
                                  optional
                                  maxLength={70}
                                  placeholder={t("app.users.enterFatherOrHusbandName")}
                                  {...additionalField('fatherOrHusbandName')}
                                  value={values.additionalProfile?.fatherOrHusbandName}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      fatherOrHusbandName: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="cnic"
                                  label={t("app.users.nationalId")}
                                  icon="bi-credit-card-2-front-fill"
                                  optional
                                  placeholder={t("app.users.enterNationalId")}
                                  {...additionalField('cnic')}
                                  value={values.additionalProfile?.cnic}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      cnic: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormNativeSelectField
                                  id="bloodGroup"
                                  label={t("app.users.bloodGroup")}
                                  icon="bi-droplet-fill"
                                  optional
                                  {...additionalField('bloodGroup')}
                                  value={values.additionalProfile?.bloodGroup}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      bloodGroup: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                  placeholder={t("app.users.selectBloodGroup")}
                                >
                                  {BloodOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.text}
                                    </option>
                                  ))}
                                </UserFormNativeSelectField>
                                <UserFormNativeSelectField
                                  id="disability"
                                  label={t("app.users.disability")}
                                  icon="bi-universal-access"
                                  optional
                                  {...additionalField('disability')}
                                  value={
                                    values.additionalProfile?.disability === true
                                      ? "true"
                                      : values.additionalProfile?.disability === false
                                        ? "false"
                                        : ""
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      disability: val === "" ? null : val === "true",
                                      disabilityDescription:
                                        val === "true"
                                          ? values.additionalProfile?.disabilityDescription
                                          : "",
                                    });
                                  }}
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                  placeholder={t("app.users.selectDisability")}
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </UserFormNativeSelectField>
                                {values.additionalProfile?.disability === true && (
                                  <UserFormTextField
                                    id="disabilityDescription"
                                    label={t("app.users.disabilityDescription")}
                                    icon="bi-card-text"
                                    required
                                    colClass="col-12"
                                    placeholder={t("app.users.enterDisabilityDescription")}
                                  {...additionalField('disabilityDescription')}
                                    as="textarea"
                                    rows={2}
                                    value={values.additionalProfile?.disabilityDescription}
                                    onChange={(e) =>
                                      setFieldValue("additionalProfile", {
                                        ...values.additionalProfile,
                                        disabilityDescription: e.target.value,
                                      })
                                    }
                                    disabled={userStore.isLoading || userStore.userDataLoading}
                                  />
                                )}
                              </div>
                              </UserFormCollapsibleSubsection>

                              <UserFormCollapsibleSubsection
                                id={ADDITIONAL_PROFILE_SECTIONS.contact}
                                icon="bi-telephone-fill"
                                title={t("app.users.contactDetailsSubsection")}
                                expanded={expandedAdditionalSections.contact}
                                onToggle={() => toggleAdditionalSection(ADDITIONAL_PROFILE_SECTIONS.contact)}
                              >
                              <div className="row g-3 mb-2">
                                <UserFormTextField
                                  id="phoneNumber"
                                  label={t("app.users.phoneNumber")}
                                  icon="bi-phone-fill"
                                  optional
                                  maxLength={30}
                                  placeholder={t("app.users.enterPhoneNumber")}
                                  {...additionalField('phoneNumber')}
                                  value={values.additionalProfile?.phoneNumber}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      phoneNumber: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="emergencyContactName"
                                  label={t("app.users.emergencyContactName")}
                                  icon="bi-person-exclamation"
                                  optional
                                  maxLength={70}
                                  placeholder={t("app.users.enterEmergencyContactName")}
                                  {...additionalField('emergencyContactName')}
                                  value={values.additionalProfile?.emergencyContactName}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      emergencyContactName: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="emergencyContactNumber"
                                  label={t("app.users.emergencyContactNumber")}
                                  icon="bi-telephone-plus-fill"
                                  optional
                                  maxLength={30}
                                  placeholder={t("app.users.enterEmergencyContactNumber")}
                                  {...additionalField('emergencyContactNumber')}
                                  value={values.additionalProfile?.emergencyContactNumber}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      emergencyContactNumber: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="passportNumber"
                                  label={t("app.users.passportNumber")}
                                  icon="bi-passport-fill"
                                  optional
                                  maxLength={50}
                                  placeholder={t("app.users.enterPassportNumber")}
                                  {...additionalField('passportNumber')}
                                  value={values.additionalProfile?.passportNumber}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      passportNumber: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="ntn"
                                  label={t("app.users.taxNumber")}
                                  icon="bi-receipt"
                                  optional
                                  maxLength={50}
                                  placeholder={t("app.users.enterTaxNumber")}
                                  {...additionalField('ntn')}
                                  value={values.additionalProfile?.ntn}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      ntn: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                              </div>
                              </UserFormCollapsibleSubsection>

                              <UserFormCollapsibleSubsection
                                id={ADDITIONAL_PROFILE_SECTIONS.address}
                                icon="bi-geo-alt-fill"
                                title={t("app.users.addressDetailsSubsection")}
                                expanded={expandedAdditionalSections.address}
                                onToggle={() => toggleAdditionalSection(ADDITIONAL_PROFILE_SECTIONS.address)}
                              >
                              <div className="row g-3">
                                <UserFormTextField
                                  id="residentialAddress"
                                  label={t("app.users.residentialAddress")}
                                  icon="bi-house-door-fill"
                                  optional
                                  maxLength={1000}
                                  placeholder={t("app.users.enterResidentialAddress")}
                                  {...additionalField('residentialAddress')}
                                  as="textarea"
                                  rows={2}
                                  value={values.additionalProfile?.residentialAddress}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      residentialAddress: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="permanentAddress"
                                  label={t("app.users.permanentAddress")}
                                  icon="bi-house-fill"
                                  optional
                                  maxLength={1000}
                                  placeholder={t("app.users.enterPermanentAddress")}
                                  {...additionalField('permanentAddress')}
                                  as="textarea"
                                  rows={2}
                                  value={values.additionalProfile?.permanentAddress}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      permanentAddress: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormTextField
                                  id="city"
                                  label={t("app.users.city")}
                                  icon="bi-building"
                                  optional
                                  maxLength={100}
                                  placeholder={t("app.users.enterCity")}
                                  {...additionalField('city')}
                                  value={values.additionalProfile?.city}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      city: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                                <UserFormCountryField
                                  id="countryId"
                                  label={t("app.users.country")}
                                  optional
                                  countries={officeStore.countries}
                                  loading={officeStore.isLoading && !officeStore.countries?.length}
                                  placeholder={t("app.users.selectCountry")}
                                  {...additionalField("countryId")}
                                  value={values.additionalProfile?.countryId}
                                  onChange={(countryId) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      countryId: countryId ?? "",
                                    })
                                  }
                                />
                                <UserFormTextField
                                  id="postalCode"
                                  label={t("app.users.postalCode")}
                                  icon="bi-mailbox"
                                  optional
                                  maxLength={20}
                                  placeholder={t("app.users.enterPostalCode")}
                                  {...additionalField('postalCode')}
                                  value={values.additionalProfile?.postalCode}
                                  onChange={(e) =>
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      postalCode: e.target.value,
                                    })
                                  }
                                  disabled={userStore.isLoading || userStore.userDataLoading}
                                />
                              </div>
                              </UserFormCollapsibleSubsection>

                              <UserFormCollapsibleSubsection
                                id={ADDITIONAL_PROFILE_SECTIONS.dependents}
                                icon="bi-people-fill"
                                title={t("app.users.dependentsSubsection")}
                                expanded={expandedAdditionalSections.dependents}
                                onToggle={() => toggleAdditionalSection(ADDITIONAL_PROFILE_SECTIONS.dependents)}
                              >
                              <p className="text-muted small mb-3">{t("app.users.dependentsDescription")}</p>
                              <div className="user-form-dependents-section">
                                {(!values.additionalProfile?.dependents || values.additionalProfile.dependents.length === 0) && (
                                  <div className="user-form-dependents-empty">
                                    <i className="bi bi-people" />
                                    <div>{t("app.users.noDependentsAdded")}</div>
                                  </div>
                                )}
                                {values.additionalProfile?.dependents?.map((dep, depIndex) => (
                                  <div key={depIndex} className="user-form-dependent-card">
                                    <div className="user-form-dependent-card__header">
                                      <span className="user-form-dependent-card__number">
                                        {t("app.users.dependentNumber", { number: depIndex + 1 })}
                                      </span>
                                      <button
                                        type="button"
                                        className="user-form-dependent-card__remove"
                                        title={t("app.users.removeDependent")}
                                        onClick={() => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated.splice(depIndex, 1);
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                      >
                                        <i className="bi bi-trash" />
                                      </button>
                                    </div>
                                    <div className="row g-3">
                                      <UserFormTextField
                                        id={`dependentName_${depIndex}`}
                                        label={t("app.users.dependentName")}
                                        icon="bi-person"
                                        required
                                        placeholder={t("app.users.enterDependentName")}
                                        value={dep.name}
                                        onChange={(e) => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated[depIndex] = { ...updated[depIndex], name: e.target.value };
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        onBlur={() =>
                                          setFieldTouched(
                                            `additionalProfile.dependents.${depIndex}.name`,
                                            true
                                          )
                                        }
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                        error={
                                          errors.additionalProfile?.dependents?.[depIndex]?.name
                                        }
                                        touched={
                                          Boolean(
                                            touched.additionalProfile?.dependents?.[depIndex]?.name
                                          ) || submitCount > 0
                                        }
                                      />
                                      <UserFormNativeSelectField
                                        id={`dependentRelationship_${depIndex}`}
                                        label={t("app.users.dependentRelationship")}
                                        icon="bi-heart"
                                        optional
                                        value={dep.relationship}
                                        onChange={(e) => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated[depIndex] = { ...updated[depIndex], relationship: e.target.value };
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                        placeholder={t("app.users.selectDependentRelationship")}
                                      >
                                        <option value="Spouse">{t("app.users.dependentRelationshipSpouse")}</option>
                                        <option value="Child">{t("app.users.dependentRelationshipChild")}</option>
                                        <option value="Parent">{t("app.users.dependentRelationshipParent")}</option>
                                        <option value="Sibling">{t("app.users.dependentRelationshipSibling")}</option>
                                        <option value="Other">{t("app.users.dependentRelationshipOther")}</option>
                                      </UserFormNativeSelectField>
                                      <UserFormTextField
                                        id={`dependentDob_${depIndex}`}
                                        label={t("app.users.dependentDateOfBirth")}
                                        icon="bi-calendar-heart"
                                        optional
                                        type="date"
                                        max={getLocalDateInputMax()}
                                        value={dep.dob}
                                        onChange={(e) => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated[depIndex] = { ...updated[depIndex], dob: e.target.value };
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                      />
                                      <UserFormNativeSelectField
                                        id={`dependentGender_${depIndex}`}
                                        label={t("app.users.dependentGender")}
                                        icon="bi-gender-ambiguous"
                                        optional
                                        value={dep.gender}
                                        onChange={(e) => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated[depIndex] = { ...updated[depIndex], gender: e.target.value };
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                        placeholder={t("app.users.selectGender")}
                                      >
                                        {getUserFormGenderOptions(t).map((opt) => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </UserFormNativeSelectField>
                                      <UserFormTextField
                                        id={`dependentContact_${depIndex}`}
                                        label={t("app.users.dependentContactNumber")}
                                        icon="bi-telephone"
                                        optional
                                        placeholder={t("app.users.enterDependentContactNumber")}
                                        value={dep.contactNumber}
                                        onChange={(e) => {
                                          const updated = [...(values.additionalProfile?.dependents || [])];
                                          updated[depIndex] = { ...updated[depIndex], contactNumber: e.target.value };
                                          setFieldValue("additionalProfile", {
                                            ...values.additionalProfile,
                                            dependents: updated,
                                          });
                                        }}
                                        disabled={userStore.isLoading || userStore.userDataLoading}
                                      />
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="user-form-dependents-add-btn"
                                  onClick={() => {
                                    const current = values.additionalProfile?.dependents || [];
                                    if (current.length >= 10) return;
                                    setFieldValue("additionalProfile", {
                                      ...values.additionalProfile,
                                      dependents: [...current, { ...EMPTY_DEPENDENT }],
                                    });
                                  }}
                                  disabled={
                                    userStore.isLoading ||
                                    userStore.userDataLoading ||
                                    (values.additionalProfile?.dependents?.length || 0) >= 10
                                  }
                                >
                                  <i className="bi bi-plus-circle" />
                                  {t("app.users.addDependent")}
                                </button>
                                {(values.additionalProfile?.dependents?.length || 0) >= 10 && (
                                  <small className="text-muted d-block mt-1">
                                    {t("app.users.maxDependentsReached")}
                                  </small>
                                )}
                              </div>
                              </UserFormCollapsibleSubsection>
                            </div>
                          </div>

                          {/* Leave Allowance Section */}
                          <div
                            style={{ transform: "none" }}
                            className="card mb-3 border-0 shadow-sm w-100"
                          >
                            <div className="card-header user-form-card-header">
                              <div className="px-5">
                                <h5 className="mb-0 fw-bold">
                                  <i className="bi bi-calendar-check-fill me-2 text-white"></i>
                                  Leave Allowance Configuration
                                </h5>
                                <p className="mb-0 mt-2 text-light small">
                                  Configure leave types and allowances for the
                                  user
                                </p>
                              </div>
                            </div>
                            <div className="card-body p-4">
                              {/* Always render all leave types for the user, even if value is 0, balanceLeaveId is null, or active is true */}
                              {values.userLeaveSettings &&
                                values.userLeaveSettings.length > 0 ? (
                                (() => {
                                  // Separate deductible and non-deductible allowances
                                  const deductibleLeaves = values.userLeaveSettings.filter(
                                    (leave) => leave.deducted === true
                                  );
                                  const nonDeductibleLeaves = values.userLeaveSettings.filter(
                                    (leave) => leave.deducted !== true
                                  );

                                  return (
                                    <>
                                      {/* Deductible Allowances Section */}
                                      {deductibleLeaves.length > 0 && (
                                        <div className="mb-4">
                                          <div className="deductible-allowances-header">
                                            <div className="deductible-allowances-header-content">
                                              <i className="bi bi-calculator-fill deductible-allowances-icon"></i>
                                              <h5 className="deductible-allowances-title">Deductible Allowances</h5>
                                            </div>
                                            {values.leaveYearStart && proratedInfo && (
                                              <button
                                                type="button"
                                                className="btn btn-link p-0 ms-auto"
                                                style={{
                                                  padding: "0",
                                                  border: "none",
                                                  background: "none",
                                                  color: "#ffc107",
                                                  fontSize: "1rem",
                                                  lineHeight: "1",
                                                  textDecoration: "none",
                                                }}
                                                onClick={() => setShowDeductibleInfoPopup(true)}
                                                title="View prorated leave calculation info"
                                              >
                                                <i className="bi bi-info-circle-fill"></i>
                                              </button>
                                            )}
                                          </div>
                                          {deductibleLeaves.map((leave) => {
                                            const index = values.userLeaveSettings.findIndex(
                                              (l) => l.id === leave.id || l.leaveTypeId === leave.leaveTypeId
                                            );

                                            return (
                                              <div
                                                key={leave.id}
                                                className="mb-3 p-3 border rounded bg-light"
                                              >
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                  <div className="flex-grow-1">
                                                    <h6 className="mb-0 fw-semibold text-dark">
                                                      {leave.name} Allowance
                                                    </h6>
                                                    {/* Accrual Information Badge */}
                                                    {(() => {
                                                      // Get the original leave type to show default accrual settings
                                                      const originalLeaveType = leaveTypesStore.leaveTypes?.find(lt => lt.id === leave.leaveTypeId || lt.id === leave.id);
                                                      const defaultAccrualFrequency = originalLeaveType?.accrualFrequency || null;
                                                      const defaultAccrualRate = originalLeaveType?.accrualRate || 0;
                                                      const defaultAccrualCap = originalLeaveType?.accrualCap || 0;

                                                      // If custom settings exist, they override the defaults - don't show default settings
                                                      const showDefaultSettings = !leave.hasCustomAccrualSettings;

                                                      return (
                                                        <div className="mt-1">
                                                          {/* Default Accrual Settings Display - only show if no custom settings */}
                                                          {showDefaultSettings && defaultAccrualFrequency && (
                                                            <div className="mb-2">
                                                              <small className="text-muted d-block mb-1" style={{ fontSize: "0.7rem" }}>
                                                                <i className="bi bi-info-circle me-1"></i>
                                                                Default Accrual Settings:
                                                              </small>
                                                              <div className="d-flex flex-wrap gap-1">
                                                                <span className="badge bg-light text-dark border me-1" style={{ fontSize: "0.7rem" }}>
                                                                  <i className="bi bi-arrow-repeat me-1"></i>
                                                                  {defaultAccrualFrequency === 'Annually' ? 'Front-loaded (Annual Grant)' :
                                                                    defaultAccrualFrequency === 'Monthly' ? 'Monthly' :
                                                                      defaultAccrualFrequency === 'BiWeekly' ? 'Bi-weekly' :
                                                                        defaultAccrualFrequency === 'Weekly' ? 'Weekly' :
                                                                          defaultAccrualFrequency === 'Quarterly' ? 'Quarterly' :
                                                                            defaultAccrualFrequency === 'Tiered' ? 'Tiered (Based on Tenure)' :
                                                                              defaultAccrualFrequency}
                                                                </span>
                                                                {defaultAccrualFrequency && defaultAccrualFrequency !== 'Tiered' && defaultAccrualFrequency !== 'Annually' && defaultAccrualRate > 0 && (
                                                                  <span className="badge bg-light text-dark border me-1" style={{ fontSize: "0.7rem" }}>
                                                                    Rate: {defaultAccrualRate} days/{defaultAccrualFrequency === 'Monthly' ? 'month' : defaultAccrualFrequency === 'BiWeekly' ? 'bi-week' : defaultAccrualFrequency === 'Weekly' ? 'week' : defaultAccrualFrequency === 'Quarterly' ? 'quarter' : 'period'}
                                                                  </span>
                                                                )}
                                                                {defaultAccrualCap > 0 && (
                                                                  <span className="badge bg-light text-dark border me-1" style={{ fontSize: "0.7rem" }}>
                                                                    <i className="bi bi-shield-check me-1"></i>
                                                                    Max: {defaultAccrualCap} days
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          )}
                                                          {/* Current/Custom Accrual Settings - only show if different from defaults or if custom settings exist */}
                                                          {(() => {
                                                            // Check if this is a Tiered leave
                                                            const isTiered = leave.accrualFrequency === 'Tiered' ||
                                                              leave.customAccrualFrequency === 'Tiered';

                                                            // For Tiered leaves, don't show custom settings info - it's not customizable per user
                                                            if (isTiered && leave.enableAccrual && leave.accrualFrequency) {
                                                              return (
                                                                <div className="mt-1">
                                                                  <small className="text-muted d-block mb-1" style={{ fontSize: "0.7rem" }}>
                                                                    <i className="bi bi-info-circle me-1"></i>
                                                                    Tiered accrual is based on tenure and cannot be customized per user.
                                                                  </small>
                                                                </div>
                                                              );
                                                            }

                                                            // Only show current settings if they differ from defaults OR if custom settings exist
                                                            if (leave.enableAccrual && leave.accrualFrequency &&
                                                              (leave.hasCustomAccrualSettings ||
                                                                leave.accrualFrequency !== defaultAccrualFrequency ||
                                                                leave.accrualRate !== defaultAccrualRate ||
                                                                leave.accrualCap !== defaultAccrualCap)) {
                                                              return (
                                                                <div>
                                                                  {leave.hasCustomAccrualSettings && (
                                                                    <>
                                                                      <span className="badge bg-warning-subtle text-warning me-2" style={{ fontSize: "0.75rem" }}>
                                                                        <i className="bi bi-star-fill me-1"></i>
                                                                        Custom Settings (Overrides Default)
                                                                      </span>
                                                                      <br className="d-md-none" />
                                                                    </>
                                                                  )}
                                                                  <span className="badge bg-info-subtle text-info me-2" style={{ fontSize: "0.75rem" }}>
                                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                                    {leave.accrualFrequency === 'Annually' ? 'Front-loaded (Annual Grant)' :
                                                                      leave.accrualFrequency === 'Monthly' ? 'Monthly' :
                                                                        leave.accrualFrequency === 'BiWeekly' ? 'Bi-weekly' :
                                                                          leave.accrualFrequency === 'Weekly' ? 'Weekly' :
                                                                            leave.accrualFrequency === 'Quarterly' ? 'Quarterly' :
                                                                              leave.accrualFrequency === 'Tiered' ? 'Tiered (Based on Tenure)' :
                                                                                leave.accrualFrequency}
                                                                  </span>
                                                                  {leave.accrualFrequency === 'Tiered' && leave.tieredRates && leave.tieredRates.length > 0 && values.empStartDate && (
                                                                    <span className="badge bg-warning-subtle text-warning" style={{ fontSize: "0.75rem" }}>
                                                                      <i className="bi bi-info-circle me-1"></i>
                                                                      {(() => {
                                                                        const empStartDate = new Date(values.empStartDate);
                                                                        const today = new Date();
                                                                        const yearsOfService = (today - empStartDate) / (1000 * 60 * 60 * 24 * 365.25);
                                                                        const applicableTier = leave.tieredRates
                                                                          .sort((a, b) => (b.years || 0) - (a.years || 0))
                                                                          .find(tier => yearsOfService >= (tier.years || 0)) || leave.tieredRates[0];
                                                                        return `${applicableTier.days || 0} days/year`;
                                                                      })()}
                                                                    </span>
                                                                  )}
                                                                  {leave.accrualFrequency && leave.accrualFrequency !== 'Tiered' && leave.accrualFrequency !== 'Annually' && leave.accrualRate > 0 && (
                                                                    <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: "0.75rem" }}>
                                                                      {leave.accrualRate} days/{leave.accrualFrequency === 'Monthly' ? 'month' : leave.accrualFrequency === 'BiWeekly' ? 'bi-week' : leave.accrualFrequency === 'Weekly' ? 'week' : leave.accrualFrequency === 'Quarterly' ? 'quarter' : 'period'}
                                                                    </span>
                                                                  )}
                                                                  {leave.accrualCap > 0 && (
                                                                    <span className="badge bg-primary-subtle text-primary" style={{ fontSize: "0.75rem" }}>
                                                                      <i className="bi bi-shield-check me-1"></i>
                                                                      Max: {leave.accrualCap} days
                                                                    </span>
                                                                  )}
                                                                  {leave.accruedBalance !== undefined && leave.accruedBalance > 0 && (
                                                                    <span className="badge bg-success-subtle text-success ms-3" style={{ fontSize: "0.75rem" }}>
                                                                      <i className="bi bi-check-circle me-1"></i>
                                                                      Accrued: {leave.accruedBalance.toFixed(2)} days
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              );
                                                            }

                                                            return null;
                                                          })()}
                                                        </div>
                                                      );
                                                    })()}
                                                  </div>
                                                  {/* Customize Accrual Settings Button - show for:
                                                    1. Create mode (always), OR
                                                    2. Edit mode (always show for deductible leaves - we're already in deductibleLeaves.map)
                                                    BUT hide for Tiered leaves (Tiered is a unique feature, not customizable per user) */}
                                                  {(() => {
                                                    // Check if this is a Tiered leave
                                                    const isTiered = leave.accrualFrequency === 'Tiered' ||
                                                      leave.customAccrualFrequency === 'Tiered' ||
                                                      leave.accrualStartFrequency === 'Tiered';

                                                    // Don't show button for Tiered leaves
                                                    if (isTiered) return false;

                                                    return (mode === "create" || mode === "edit");
                                                  })() && (
                                                      <div className={!leave.hasCustomAccrualSettings && (leave.accrualFrequency === 'Annually' || leave.accrualFrequency === null || !leave.accrualFrequency) ? '' : 'ms-auto'} style={!leave.hasCustomAccrualSettings && (leave.accrualFrequency === 'Annually' || leave.accrualFrequency === null || !leave.accrualFrequency) ? { marginRight: '2%' } : {}}>
                                                        <button
                                                          type="button"
                                                          className="btn btn-sm btn-outline-primary"
                                                          onClick={() => {
                                                            const isExpanded = expandedAccrualSettings[leave.leaveTypeId || leave.id];
                                                            setExpandedAccrualSettings({
                                                              ...expandedAccrualSettings,
                                                              [leave.leaveTypeId || leave.id]: !isExpanded
                                                            });
                                                            // Initialize editing state with current values
                                                            if (!isExpanded && !editingAccrualSettings[leave.leaveTypeId || leave.id]) {
                                                              const leaveTypeId = leave.leaveTypeId || leave.id;
                                                              // Don't set customAccrualFrequency if current is Annually - this allows section to show when button is clicked
                                                              // User can then select Annually in dropdown if they want, which will hide it
                                                              const currentFreq = leave.accrualFrequency || leave.customAccrualFrequency;

                                                              // Initialize division units if they don't exist
                                                              // Priority: 1) Existing state, 2) Leave type defaults, 3) Default to 'Days'
                                                              if (!customAccrualRateUnits[leaveTypeId]) {
                                                                const defaultLeaveType = leaveTypesStore.leaveTypes?.find(lt => lt.id === leaveTypeId);
                                                                const rateDivision = leave.customAccrualRateDivision ?? defaultLeaveType?.accrualRateDivision ?? 'Days';
                                                                const isHours = (typeof rateDivision === 'number' && rateDivision === 1) ||
                                                                  (typeof rateDivision === 'string' && (rateDivision === 'Hours' || rateDivision.toLowerCase() === 'hours'));
                                                                setCustomAccrualRateUnits(prev => ({
                                                                  ...prev,
                                                                  [leaveTypeId]: isHours ? 'Hours' : 'Days'
                                                                }));
                                                              }

                                                              if (!customAccrualCapUnits[leaveTypeId]) {
                                                                const defaultLeaveType = leaveTypesStore.leaveTypes?.find(lt => lt.id === leaveTypeId);
                                                                const capDivision = leave.customAccrualCapDivision ?? defaultLeaveType?.accrualCapDivision ?? 'Days';
                                                                const isHours = (typeof capDivision === 'number' && capDivision === 1) ||
                                                                  (typeof capDivision === 'string' && (capDivision === 'Hours' || capDivision.toLowerCase() === 'hours'));
                                                                setCustomAccrualCapUnits(prev => ({
                                                                  ...prev,
                                                                  [leaveTypeId]: isHours ? 'Hours' : 'Days'
                                                                }));
                                                              }

                                                              setEditingAccrualSettings({
                                                                ...editingAccrualSettings,
                                                                [leaveTypeId]: {
                                                                  enableAccrual: leave.enableAccrual !== undefined && leave.enableAccrual !== null
                                                                    ? leave.enableAccrual
                                                                    : true,
                                                                  // Only set frequency if it's not Annually - allows section to show when button clicked
                                                                  customAccrualFrequency: currentFreq === 'Annually' ? undefined : (leave.accrualFrequency || 'Annually'),
                                                                  customAccrualRate: leave.accrualRate || 0,
                                                                  customAccrualCap: leave.accrualCap || 0,
                                                                  accrualStartFrequency: leave.accrualFrequency || 'Annually',
                                                                  accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
                                                                  accrualEndDate: leave.accrualEndDate || null,
                                                                }
                                                              });
                                                            }
                                                          }}
                                                          style={{ fontSize: "0.875rem" }}
                                                        >
                                                          <i className={`bi bi-${expandedAccrualSettings[leave.leaveTypeId || leave.id] ? 'chevron-up' : 'chevron-down'} me-1`}></i>
                                                          {expandedAccrualSettings[leave.leaveTypeId || leave.id] ? 'Hide' : 'Customize'} Accrual Settings
                                                        </button>
                                                      </div>
                                                    )}
                                                  {/* Hide Days/Hours toggle when custom settings exist or when accrual frequency is not Annually */}
                                                  {(() => {
                                                    // Check if custom accrual settings are being edited
                                                    const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                    const customFrequency = editingSettings?.customAccrualFrequency;

                                                    // If editing custom settings and frequency is not Annually, hide toggle
                                                    if (expandedAccrualSettings[leave.leaveTypeId || leave.id] && customFrequency && customFrequency !== 'Annually') {
                                                      return null;
                                                    }

                                                    // Otherwise, show toggle only if no custom settings and frequency is Annually
                                                    if (!leave.hasCustomAccrualSettings && (leave.accrualFrequency === 'Annually' || leave.accrualFrequency === null || !leave.accrualFrequency)) {
                                                      return (
                                                        <div className="d-flex gap-3">
                                                          <div className="form-check form-check-inline">
                                                            <input
                                                              className="form-check-input"
                                                              type="radio"
                                                              name={`userLeaveSettings[${index}].type`}
                                                              id={`days-${leave.id}`}
                                                              value="Days"
                                                              checked={
                                                                values.userLeaveSettings[index]
                                                                  .type === "Days"
                                                              }
                                                              onChange={() => {
                                                                const currentValue = values.userLeaveSettings[index]?.value || 0;
                                                                const currentType = values.userLeaveSettings[index]?.type || "Days";
                                                                // Convert from Hours to Days: divide by 8
                                                                const convertedValue = currentType === "Hours"
                                                                  ? Math.round((currentValue / 8) * 100) / 100  // Round to 2 decimal places
                                                                  : currentValue;
                                                                setFieldValue(
                                                                  `userLeaveSettings[${index}].type`,
                                                                  "Days"
                                                                );
                                                                setFieldValue(
                                                                  `userLeaveSettings[${index}].value`,
                                                                  convertedValue
                                                                );
                                                              }}
                                                            />
                                                            <label
                                                              className="form-check-label fw-semibold"
                                                              htmlFor={`days-${leave.id}`}
                                                            >
                                                              Days
                                                            </label>
                                                          </div>
                                                          <div className="form-check form-check-inline">
                                                            <input
                                                              className="form-check-input"
                                                              type="radio"
                                                              name={`userLeaveSettings[${index}].type`}
                                                              id={`hours-${leave.id}`}
                                                              value="Hours"
                                                              checked={
                                                                values.userLeaveSettings[index]
                                                                  .type === "Hours"
                                                              }
                                                              onChange={() => {
                                                                const currentValue = values.userLeaveSettings[index]?.value || 0;
                                                                const currentType = values.userLeaveSettings[index]?.type || "Days";
                                                                // Convert from Days to Hours: multiply by 8
                                                                const convertedValue = currentType === "Days"
                                                                  ? currentValue * 8
                                                                  : currentValue;
                                                                setFieldValue(
                                                                  `userLeaveSettings[${index}].type`,
                                                                  "Hours"
                                                                );
                                                                setFieldValue(
                                                                  `userLeaveSettings[${index}].value`,
                                                                  convertedValue
                                                                );
                                                              }}
                                                            />
                                                            <label
                                                              className="form-check-label fw-semibold"
                                                              htmlFor={`hours-${leave.id}`}
                                                            >
                                                              Hours
                                                            </label>
                                                          </div>
                                                        </div>
                                                      );
                                                    }
                                                    return null;
                                                  })()}
                                                </div>

                                                {/* Custom Accrual Settings Section */}
                                                {expandedAccrualSettings[leave.leaveTypeId || leave.id] && (mode === "create" || mode === "edit") && (() => {
                                                  // Check if this is a Tiered leave - don't show custom settings for Tiered
                                                  const isTiered = leave.accrualFrequency === 'Tiered' ||
                                                    leave.customAccrualFrequency === 'Tiered' ||
                                                    leave.accrualStartFrequency === 'Tiered';

                                                  // Hide custom settings section for Tiered leaves only
                                                  return !isTiered;
                                                })() && (
                                                    <div className="mt-3 p-3 border rounded bg-white" style={{ borderColor: "#667eea" }}>
                                                      <h6 className="mb-3 fw-semibold text-primary">
                                                        <i className="bi bi-gear-fill me-2"></i>
                                                        Custom Accrual Settings
                                                      </h6>
                                                      <div className="row g-3">
                                                        {/* Accrual Frequency and Accrual Start Date on same row */}
                                                        <div className="col-12 col-md-6">
                                                          <label className="form-label fw-semibold mb-2">
                                                            Accrual Frequency
                                                          </label>
                                                          <select
                                                            className="form-select"
                                                            value={editingAccrualSettings[leave.leaveTypeId || leave.id]?.customAccrualFrequency || leave.accrualFrequency || 'Annually'}
                                                            onChange={(e) => {
                                                              const leaveTypeId = leave.leaveTypeId || leave.id;
                                                              const newFrequency = e.target.value;
                                                              const updatedSettings = {
                                                                ...editingAccrualSettings[leaveTypeId],
                                                                customAccrualFrequency: newFrequency
                                                              };

                                                              // If switching to Annually, clear custom accrual settings to show allowance fields
                                                              if (newFrequency === 'Annually') {
                                                                updatedSettings.enableAccrual = false;
                                                                updatedSettings.customAccrualRate = 0;
                                                                updatedSettings.customAccrualCap = 0;
                                                              }

                                                              setEditingAccrualSettings({
                                                                ...editingAccrualSettings,
                                                                [leaveTypeId]: updatedSettings
                                                              });
                                                            }}
                                                          >
                                                            {ACCRUAL_FREQUENCY_OPTIONS.map((option) => (
                                                              <option key={option.value} value={option.value}>
                                                                {option.label}
                                                              </option>
                                                            ))}
                                                          </select>
                                                          <small className="text-muted">Tiered rates are not customizable per user</small>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                          <label className="form-label fw-semibold mb-2">
                                                            Accrual Start Date
                                                          </label>
                                                          <select
                                                            className="form-select"
                                                            value={editingAccrualSettings[leave.leaveTypeId || leave.id]?.accrualStartType || leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START}
                                                            onChange={(e) => {
                                                              const leaveTypeId = leave.leaveTypeId || leave.id;
                                                              setEditingAccrualSettings({
                                                                ...editingAccrualSettings,
                                                                [leaveTypeId]: {
                                                                  ...editingAccrualSettings[leaveTypeId],
                                                                  accrualStartType: e.target.value
                                                                }
                                                              });
                                                            }}
                                                          >
                                                            {ACCRUAL_START_DATE_OPTIONS.map((option) => (
                                                              <option key={option.value} value={option.value}>
                                                                {option.label}
                                                              </option>
                                                            ))}
                                                          </select>
                                                        </div>
                                                        {(() => {
                                                          // Hide Accrual Rate and Maximum Balance for Annually frequency
                                                          const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                          const selectedFrequency = editingSettings?.customAccrualFrequency;
                                                          const currentFrequency = leave.accrualFrequency || leave.customAccrualFrequency;
                                                          const effectiveFrequency = selectedFrequency !== undefined ? selectedFrequency : currentFrequency;
                                                          const isAnnually = effectiveFrequency === 'Annually';

                                                          return !isAnnually && (() => {
                                                            const leaveTypeId = leave.leaveTypeId || leave.id;

                                                            // Use the same unit for both Rate and Cap (synchronized)
                                                            // Priority: rateUnit, then capUnit, then default to 'Days'
                                                            const sharedUnit = customAccrualRateUnits[leaveTypeId] || customAccrualCapUnits[leaveTypeId] || 'Days';

                                                            const currentRate = editingAccrualSettings[leaveTypeId]?.customAccrualRate ?? leave.accrualRate ?? 0;
                                                            const rateDisplayValue = sharedUnit === 'Hours' && currentRate > 0
                                                              ? daysToHours(currentRate)
                                                              : (currentRate || '');

                                                            const currentCap = editingAccrualSettings[leaveTypeId]?.customAccrualCap ?? leave.accrualCap ?? 0;
                                                            const capDisplayValue = sharedUnit === 'Hours' && currentCap > 0
                                                              ? daysToHours(currentCap)
                                                              : (currentCap || '');

                                                            return (
                                                              <div className="col-12">
                                                                {/* Light border container for Unit, Accrual Rate, and Maximum Balance */}
                                                                <div className="p-3 border rounded" style={{
                                                                  backgroundColor: "#f8f9fa",
                                                                  borderColor: "#dee2e6"
                                                                }}>
                                                                  <div className="row g-3">
                                                                    {/* Shared Days/Hours Radio Buttons - displayed once above both fields */}
                                                                    <div className="col-12">
                                                                      <label className="form-label fw-semibold mb-2">
                                                                        Unit
                                                                      </label>
                                                                      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                          <input
                                                                            type="radio"
                                                                            name={`customAccrualUnit-${leaveTypeId}`}
                                                                            id={`customAccrualDays-${leaveTypeId}`}
                                                                            checked={sharedUnit === 'Days'}
                                                                            onChange={async () => {
                                                                              // Update both rate and cap units to keep them synchronized
                                                                              setCustomAccrualRateUnits(prev => ({ ...prev, [leaveTypeId]: 'Days' }));
                                                                              setCustomAccrualCapUnits(prev => ({ ...prev, [leaveTypeId]: 'Days' }));
                                                                              // Save immediately if userId exists and we have settings
                                                                              if (userId && editingAccrualSettings[leaveTypeId]) {
                                                                                setTimeout(async () => {
                                                                                  await saveAccrualSettingsForUser(userId, { userLeaveSettings: values.userLeaveSettings }, true);
                                                                                }, 0);
                                                                              }
                                                                            }}
                                                                            style={{ cursor: "pointer", margin: 0 }}
                                                                          />
                                                                          <label
                                                                            htmlFor={`customAccrualDays-${leaveTypeId}`}
                                                                            style={{ cursor: "pointer", margin: 0, fontSize: "0.9em", fontWeight: "500" }}
                                                                          >
                                                                            Days
                                                                          </label>
                                                                        </div>
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                          <input
                                                                            type="radio"
                                                                            name={`customAccrualUnit-${leaveTypeId}`}
                                                                            id={`customAccrualHours-${leaveTypeId}`}
                                                                            checked={sharedUnit === 'Hours'}
                                                                            onChange={async () => {
                                                                              // Update both rate and cap units to keep them synchronized
                                                                              setCustomAccrualRateUnits(prev => ({ ...prev, [leaveTypeId]: 'Hours' }));
                                                                              setCustomAccrualCapUnits(prev => ({ ...prev, [leaveTypeId]: 'Hours' }));
                                                                              // Save immediately if userId exists and we have settings
                                                                              if (userId && editingAccrualSettings[leaveTypeId]) {
                                                                                setTimeout(async () => {
                                                                                  await saveAccrualSettingsForUser(userId, { userLeaveSettings: values.userLeaveSettings }, true);
                                                                                }, 0);
                                                                              }
                                                                            }}
                                                                            style={{ cursor: "pointer", margin: 0 }}
                                                                          />
                                                                          <label
                                                                            htmlFor={`customAccrualHours-${leaveTypeId}`}
                                                                            style={{ cursor: "pointer", margin: 0, fontSize: "0.9em", fontWeight: "500" }}
                                                                          >
                                                                            Hours
                                                                          </label>
                                                                        </div>
                                                                      </div>
                                                                    </div>

                                                                    {/* Accrual Rate and Maximum Balance on same row */}
                                                                    <div className="col-12 col-md-6">
                                                                      <label className="form-label fw-semibold mb-2">
                                                                        Accrual Rate
                                                                      </label>
                                                                      <div className="d-flex align-items-center">
                                                                        <input
                                                                          type="number"
                                                                          className="form-control"
                                                                          step={sharedUnit === 'Hours' ? "0.01" : "0.01"}
                                                                          min="0"
                                                                          value={rateDisplayValue}
                                                                          onChange={(e) => {
                                                                            const inputValue = parseFloat(e.target.value) || 0;
                                                                            // Convert to days if in hours mode, otherwise use as is
                                                                            const daysValue = sharedUnit === 'Hours'
                                                                              ? hoursToDays(inputValue)
                                                                              : inputValue;
                                                                            setEditingAccrualSettings({
                                                                              ...editingAccrualSettings,
                                                                              [leaveTypeId]: {
                                                                                ...editingAccrualSettings[leaveTypeId],
                                                                                customAccrualRate: daysValue
                                                                              }
                                                                            });
                                                                          }}
                                                                        />
                                                                        <span className="ms-2 fw-semibold">{sharedUnit}</span>
                                                                      </div>
                                                                      <small className="text-muted">Leave empty or 0 to use leave type default</small>
                                                                    </div>

                                                                    <div className="col-12 col-md-6">
                                                                      <label className="form-label fw-semibold mb-2">
                                                                        Maximum Balance (Accrual Cap)
                                                                      </label>
                                                                      <div className="d-flex align-items-center">
                                                                        <input
                                                                          type="number"
                                                                          className="form-control"
                                                                          step={sharedUnit === 'Hours' ? "0.01" : "0.01"}
                                                                          min="0"
                                                                          value={capDisplayValue}
                                                                          onChange={(e) => {
                                                                            const inputValue = parseFloat(e.target.value) || 0;
                                                                            // Convert to days if in hours mode, otherwise use as is
                                                                            const daysValue = sharedUnit === 'Hours'
                                                                              ? hoursToDays(inputValue)
                                                                              : inputValue;
                                                                            setEditingAccrualSettings({
                                                                              ...editingAccrualSettings,
                                                                              [leaveTypeId]: {
                                                                                ...editingAccrualSettings[leaveTypeId],
                                                                                customAccrualCap: daysValue
                                                                              }
                                                                            });
                                                                          }}
                                                                        />
                                                                        <span className="ms-2 fw-semibold">{sharedUnit}</span>
                                                                      </div>
                                                                      <small className="text-muted">Maximum {sharedUnit.toLowerCase()} that can be accrued (0 = unlimited)</small>
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            );
                                                          })();
                                                        })()}
                                                        {(() => {
                                                          // Hide Accrual End Date for Annually frequency
                                                          const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                          const selectedFrequency = editingSettings?.customAccrualFrequency;
                                                          const currentFrequency = leave.accrualFrequency || leave.customAccrualFrequency;
                                                          const effectiveFrequency = selectedFrequency !== undefined ? selectedFrequency : currentFrequency;
                                                          const isAnnually = effectiveFrequency === 'Annually';

                                                          return !isAnnually && (
                                                            <div className="col-12 col-md-6">
                                                              <label className="form-label fw-semibold mb-2">
                                                                Accrual End Date (Optional)
                                                              </label>
                                                              <input
                                                                type="date"
                                                                className="form-control"
                                                                value={editingAccrualSettings[leave.leaveTypeId || leave.id]?.accrualEndDate || ''}
                                                                onChange={(e) => {
                                                                  const leaveTypeId = leave.leaveTypeId || leave.id;
                                                                  setEditingAccrualSettings({
                                                                    ...editingAccrualSettings,
                                                                    [leaveTypeId]: {
                                                                      ...editingAccrualSettings[leaveTypeId],
                                                                      accrualEndDate: e.target.value || null
                                                                    }
                                                                  });
                                                                }}
                                                              />
                                                              <small className="text-muted">Leave empty for no end date</small>
                                                            </div>
                                                          );
                                                        })()}
                                                        {(() => {
                                                          // Hide Enable Accrual checkbox for Annually frequency
                                                          const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                          const selectedFrequency = editingSettings?.customAccrualFrequency;
                                                          const currentFrequency = leave.accrualFrequency || leave.customAccrualFrequency;
                                                          const effectiveFrequency = selectedFrequency !== undefined ? selectedFrequency : currentFrequency;
                                                          const isAnnually = effectiveFrequency === 'Annually';

                                                          return !isAnnually && (
                                                            <div className="col-12">
                                                              <div className="form-check">
                                                                <input
                                                                  className="form-check-input"
                                                                  type="checkbox"
                                                                  checked={
                                                                    (() => {
                                                                      const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                                      // Use editingSettings if available, otherwise fall back to leave.enableAccrual
                                                                      if (editingSettings?.enableAccrual !== undefined && editingSettings?.enableAccrual !== null) {
                                                                        return editingSettings.enableAccrual !== false;
                                                                      }
                                                                      return leave.enableAccrual !== false;
                                                                    })()
                                                                  }
                                                                  onChange={(e) => {
                                                                    const leaveTypeId = leave.leaveTypeId || leave.id;
                                                                    const checked = e.target.checked;

                                                                    // Update editingAccrualSettings
                                                                    setEditingAccrualSettings({
                                                                      ...editingAccrualSettings,
                                                                      [leaveTypeId]: {
                                                                        ...editingAccrualSettings[leaveTypeId],
                                                                        enableAccrual: checked
                                                                      }
                                                                    });

                                                                    // ALSO update the form state to ensure it's saved even if editingAccrualSettings isn't used
                                                                    setFieldValue(
                                                                      `userLeaveSettings[${index}].enableAccrual`,
                                                                      checked
                                                                    );

                                                                    // For non-Annually frequencies, sync active with enableAccrual
                                                                    // This checkbox is only shown for non-Annually frequencies, so always sync
                                                                    setFieldValue(
                                                                      `userLeaveSettings[${index}].active`,
                                                                      checked
                                                                    );

                                                                    // Mark as touched
                                                                    setFieldTouched(
                                                                      `userLeaveSettings[${index}].enableAccrual`,
                                                                      true
                                                                    );
                                                                    setFieldTouched(
                                                                      `userLeaveSettings[${index}].active`,
                                                                      true
                                                                    );
                                                                  }}
                                                                />
                                                                <label className="form-check-label">
                                                                  Enable Accrual
                                                                </label>
                                                              </div>
                                                            </div>
                                                          );
                                                        })()}
                                                        <div className="col-12 d-flex gap-2 justify-content-end">
                                                          <button
                                                            type="button"
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={() => {
                                                              const leaveTypeId = leave.leaveTypeId || leave.id;
                                                              setExpandedAccrualSettings({
                                                                ...expandedAccrualSettings,
                                                                [leaveTypeId]: false
                                                              });
                                                              // Reset to original values
                                                              setEditingAccrualSettings({
                                                                ...editingAccrualSettings,
                                                                [leaveTypeId]: {
                                                                  enableAccrual: leave.enableAccrual !== false,
                                                                  customAccrualFrequency: leave.accrualFrequency || 'Annually',
                                                                  customAccrualRate: leave.accrualRate || 0,
                                                                  customAccrualCap: leave.accrualCap || 0,
                                                                  accrualStartType: leave.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START,
                                                                  accrualEndDate: leave.accrualEndDate || null,
                                                                }
                                                              });
                                                            }}
                                                          >
                                                            Cancel
                                                          </button>
                                                          {/* Hide Save button - settings will be saved with main form submission
                                                      {mode === "create" && (
                                                        <small className="text-muted d-flex align-items-center">
                                                          <i className="bi bi-info-circle me-1"></i>
                                                          Custom settings will be saved after user creation
                                                        </small>
                                                      )}
                                                      {mode === "edit" && (
                                                        <small className="text-muted d-flex align-items-center">
                                                          <i className="bi bi-info-circle me-1"></i>
                                                          Custom settings will be saved when you click Update
                                                        </small>
                                                      )} */}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}

                                                {/* Show Total Allowance, Utilized, Remaining, and Status only if:
                                                  1. Accrual frequency is "Annually" (default Annual Grant), AND
                                                  2. User does NOT have custom accrual settings (custom settings hide these fields), AND
                                                  3. If custom accrual settings are being edited, the selected frequency is "Annually" */}
                                                {(() => {
                                                  // Check if custom accrual settings are being edited
                                                  const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                  const customFrequency = editingSettings?.customAccrualFrequency;
                                                  const isEditing = expandedAccrualSettings[leave.leaveTypeId || leave.id];

                                                  // Determine the effective frequency: use editing frequency if editing, otherwise use leave frequency
                                                  const effectiveFrequency = isEditing && customFrequency !== undefined
                                                    ? customFrequency
                                                    : (leave.accrualFrequency || null);

                                                  // Determine if we're using Annual Grant (Annually frequency means no accrual, just fixed allowance)
                                                  const isAnnualGrant = (effectiveFrequency === 'Annually' || effectiveFrequency === null);

                                                  // If frequency is not Annually and we're editing, hide allowance fields (show accrual settings instead)
                                                  if (isEditing && !isAnnualGrant) {
                                                    return null;
                                                  }

                                                  // Show allowance fields if frequency is Annually (Annual Grant)
                                                  // Annual Grant always shows allowance fields, regardless of hasCustomSettings
                                                  // because Annual Grant doesn't use accrual - it's a fixed allowance
                                                  const showAllowanceFields = isAnnualGrant;

                                                  return showAllowanceFields ? (
                                                    <div className="row g-3">
                                                      <div className="col-12 col-md-3">
                                                        {(() => {
                                                          // Get original leave type for default value
                                                          const originalLeaveType = leaveTypesStore.leaveTypes?.find(lt => lt.id === leave.leaveTypeId || lt.id === leave.id);
                                                          const defaultLeaveValue = originalLeaveType?.defaultLeaveCount || leave.value || 0;

                                                          // Check if this is create mode, Front-loaded (Annual Grant), and NOT using Customize Accrual Settings
                                                          const isCreateModeFrontLoaded = mode === "create" &&
                                                            isAnnualGrant &&
                                                            !leave.hasCustomAccrualSettings &&
                                                            !isEditing;

                                                          // Calculate prorated value for create mode Front-loaded
                                                          const proratedValue = isCreateModeFrontLoaded && proratedInfo && defaultLeaveValue > 0
                                                            ? defaultLeaveValue * proratedInfo.prorationFactor
                                                            : null;

                                                          // Always use the form value for textbox (allows user to edit)
                                                          const textboxValue = values.userLeaveSettings[index]?.value ?? "";

                                                          // Clear ref if leaveYearStart changed
                                                          if (previousLeaveYearStartRef.current !== null &&
                                                            previousLeaveYearStartRef.current !== values.leaveYearStart) {
                                                            proratedValuesSetRef.current.clear();
                                                          }
                                                          previousLeaveYearStartRef.current = values.leaveYearStart;

                                                          // Set the prorated value in form if conditions are met (only once per leave type, and only if value is default/empty)
                                                          if (isCreateModeFrontLoaded && proratedValue !== null) {
                                                            const leaveId = leave.leaveTypeId || leave.id;
                                                            const key = `${leaveId}_${values.leaveYearStart}`;
                                                            const currentFormValue = values.userLeaveSettings[index]?.value;

                                                            // Only set if we haven't set it before and current value is default or empty
                                                            if (!proratedValuesSetRef.current.has(key) &&
                                                              (currentFormValue === defaultLeaveValue ||
                                                                currentFormValue === 0 ||
                                                                currentFormValue === null ||
                                                                currentFormValue === undefined)) {
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].value`,
                                                                Math.round(proratedValue * 100) / 100
                                                              );
                                                              proratedValuesSetRef.current.add(key);
                                                            }
                                                          }

                                                          return (
                                                            <>
                                                              <label className="form-label fw-semibold mb-2">
                                                                Total Allowance{" "}
                                                                {isCreateModeFrontLoaded && defaultLeaveValue > 0 && (
                                                                  <span className="text-muted fw-normal" style={{ fontSize: "0.875rem" }}>
                                                                    (Default: {defaultLeaveValue} {values.userLeaveSettings[index]?.type || "Days"})
                                                                  </span>
                                                                )}
                                                                <span className="text-danger">*</span>
                                                                {leave.accrualMethod && leave.accrualMethod !== 'None' && (
                                                                  <i className="bi bi-info-circle ms-1 text-info"
                                                                    style={{ fontSize: "0.875rem", cursor: "help" }}
                                                                    title="This leave type uses accrual. The balance may be calculated automatically based on accrual settings."></i>
                                                                )}
                                                              </label>
                                                              <div className="d-flex align-items-stretch">
                                                                <span
                                                                  className="bg-light border border-end-0 d-flex align-items-center px-3"
                                                                  style={{
                                                                    fontSize: "0.875rem",
                                                                    minWidth: "auto",
                                                                    flexShrink: 0,
                                                                  }}
                                                                >
                                                                  <i className="bi bi-plus-circle-fill"></i>
                                                                </span>
                                                                <input
                                                                  type="number"
                                                                  step="0.01"
                                                                  className={`form-control border rounded-0 ${errors.userLeaveSettings?.[index]
                                                                    ?.value &&
                                                                    touched.userLeaveSettings?.[index]
                                                                      ?.value
                                                                    ? "is-invalid"
                                                                    : ""
                                                                    }`}
                                                                  style={{ flex: "1 1 auto" }}
                                                                  placeholder={t("app.users.enterLeaveAllowance", { name: leave.name || "" })}
                                                                  value={textboxValue}
                                                                  onChange={(e) => {
                                                                    const inputValue = e.target.value;
                                                                    // Allow empty string or valid number
                                                                    if (inputValue === "" || !isNaN(inputValue)) {
                                                                      setFieldValue(
                                                                        `userLeaveSettings[${index}].value`,
                                                                        inputValue === "" ? "" : Number(inputValue)
                                                                      );
                                                                    }
                                                                  }}
                                                                  onBlur={() =>
                                                                    setFieldTouched(
                                                                      `userLeaveSettings[${index}].value`,
                                                                      true
                                                                    )
                                                                  }
                                                                  disabled={
                                                                    values.userLeaveSettings[index]
                                                                      ?.active === false
                                                                  }
                                                                />
                                                                <span
                                                                  className="bg-light border border-start-0 d-flex align-items-center px-3"
                                                                  style={{
                                                                    fontSize: "0.875rem",
                                                                    minWidth: "auto",
                                                                    flexShrink: 0,
                                                                  }}
                                                                >
                                                                  {values.userLeaveSettings[index].type}
                                                                </span>
                                                              </div>
                                                              {errors.userLeaveSettings?.[index]
                                                                ?.value &&
                                                                touched.userLeaveSettings?.[index]
                                                                  ?.value && (
                                                                  <div className="invalid-feedback">
                                                                    {
                                                                      errors.userLeaveSettings[index]
                                                                        .value
                                                                    }
                                                                  </div>
                                                                )}
                                                              {/* Show prorated message only if value matches prorated value (user hasn't edited) */}
                                                              {(() => {
                                                                // Check if this is edit mode with Front-loaded (Annual Grant) without Customize Accrual Settings
                                                                const isEditModeFrontLoaded = mode === "edit" &&
                                                                  isAnnualGrant &&
                                                                  !leave.hasCustomAccrualSettings &&
                                                                  !isEditing;

                                                                // Hide blue message in edit mode for Front-loaded without custom accrual settings
                                                                if (isEditModeFrontLoaded) {
                                                                  return null;
                                                                }

                                                                const leaveValue = values.userLeaveSettings[index]?.value;

                                                                // Calculate what the prorated value should be
                                                                const expectedProratedValue = isCreateModeFrontLoaded && proratedValue !== null
                                                                  ? Math.round(proratedValue * 100) / 100
                                                                  : (leaveValue && proratedInfo
                                                                    ? Math.round((leaveValue * proratedInfo.prorationFactor) * 100) / 100
                                                                    : 0);

                                                                // For create mode Front-loaded, check if current value matches the prorated value
                                                                // For other cases, calculate from current value
                                                                const calculatedProratedValue = leaveValue && proratedInfo
                                                                  ? Math.round((leaveValue * proratedInfo.prorationFactor) * 100) / 100
                                                                  : 0;

                                                                // Only show if we have valid values and the calculated prorated value is greater than 0
                                                                if (!proratedInfo || calculatedProratedValue <= 0) {
                                                                  return null;
                                                                }

                                                                // For create mode Front-loaded: only show if current value matches the prorated value (user hasn't edited)
                                                                // For other cases: always show
                                                                if (isCreateModeFrontLoaded && proratedValue !== null) {
                                                                  const roundedProratedValue = Math.round(proratedValue * 100) / 100;
                                                                  const roundedCurrentValue = leaveValue ? Math.round(leaveValue * 100) / 100 : 0;
                                                                  // Hide message if user has entered a different value (with small tolerance for floating point)
                                                                  if (Math.abs(roundedCurrentValue - roundedProratedValue) > 0.01) {
                                                                    return null;
                                                                  }
                                                                }

                                                                return (
                                                                  <div className="mt-2">
                                                                    <small className="text-info d-flex align-items-center">
                                                                      <i className="bi bi-info-circle-fill me-1" style={{ fontSize: "0.875rem" }}></i>
                                                                      <span>
                                                                        The user will receive{" "}
                                                                        <strong className="text-info">
                                                                          {calculatedProratedValue}{" "}
                                                                          {values.userLeaveSettings[index].type}
                                                                        </strong>{" "}
                                                                        for the current year
                                                                        {proratedInfo.monthsRemaining < 12 && (
                                                                          <span className="text-muted">
                                                                            {" "}
                                                                            (prorated based on {proratedInfo.monthsRemaining} remaining months)
                                                                          </span>
                                                                        )}
                                                                      </span>
                                                                    </small>
                                                                  </div>
                                                                );
                                                              })()}
                                                            </>
                                                          );
                                                        })()}
                                                      </div>
                                                      <div className="col-12 col-md-3">
                                                        <label className="form-label fw-semibold mb-2">
                                                          Utilized
                                                        </label>
                                                        <div className="d-flex align-items-stretch">
                                                          <span
                                                            className="bg-light border border-end-0 d-flex align-items-center px-3"
                                                            style={{
                                                              fontSize: "0.875rem",
                                                              minWidth: "auto",
                                                              flexShrink: 0,
                                                            }}
                                                          >
                                                            <i className="bi bi-dash-circle-fill"></i>
                                                          </span>
                                                          <input
                                                            type="number"
                                                            className="form-control border-0"
                                                            style={{ flex: "1 1 auto" }}
                                                            value={values.userLeaveSettings[index]?.usedLeaves || 0}
                                                            disabled
                                                            readOnly
                                                          />
                                                          <span
                                                            className="bg-light border border-start-0 d-flex align-items-center px-3"
                                                            style={{
                                                              fontSize: "0.875rem",
                                                              minWidth: "auto",
                                                              flexShrink: 0,
                                                            }}
                                                          >
                                                            {values.userLeaveSettings[index].type}
                                                          </span>
                                                        </div>
                                                        <small className="text-muted">
                                                          Sum of previously used/disabled balances
                                                        </small>
                                                      </div>
                                                      <div className="col-12 col-md-3">
                                                        <label className="form-label fw-semibold mb-2">
                                                          Remaining
                                                        </label>
                                                        <div className="d-flex align-items-stretch">
                                                          <span
                                                            className="bg-light border border-end-0 d-flex align-items-center px-3"
                                                            style={{
                                                              fontSize: "0.875rem",
                                                              minWidth: "auto",
                                                              flexShrink: 0,
                                                            }}
                                                          >
                                                            <i className="bi bi-arrow-repeat"></i>
                                                          </span>
                                                          <input
                                                            type="number"
                                                            className="form-control border-0"
                                                            style={{ flex: "1 1 auto" }}
                                                            value={Math.max(
                                                              (values.userLeaveSettings[index]
                                                                ?.value || 0) -
                                                              (values.userLeaveSettings[index]?.usedLeaves || 0),
                                                              0
                                                            )}
                                                            disabled
                                                            readOnly
                                                          />
                                                          <span
                                                            className="bg-light border border-start-0 d-flex align-items-center px-3"
                                                            style={{
                                                              fontSize: "0.875rem",
                                                              minWidth: "auto",
                                                              flexShrink: 0,
                                                            }}
                                                          >
                                                            {values.userLeaveSettings[index].type}
                                                          </span>
                                                        </div>
                                                        <small className="text-muted">
                                                          Allowance minus utilized
                                                        </small>
                                                      </div>
                                                      {/* Status field - in same row as allowance fields */}
                                                      {(() => {
                                                        // Check if this is a Tiered leave
                                                        const isTiered = leave.accrualFrequency === 'Tiered' ||
                                                          leave.customAccrualFrequency === 'Tiered' ||
                                                          leave.accrualStartFrequency === 'Tiered';

                                                        // Check if custom accrual settings are expanded and frequency is Annually
                                                        const editingSettings = editingAccrualSettings[leave.leaveTypeId || leave.id];
                                                        const selectedFrequency = editingSettings?.customAccrualFrequency;
                                                        const currentFrequency = leave.accrualFrequency || leave.customAccrualFrequency;
                                                        const effectiveFrequency = selectedFrequency !== undefined ? selectedFrequency : currentFrequency;
                                                        const isAnnually = effectiveFrequency === 'Annually';
                                                        const isExpanded = expandedAccrualSettings[leave.leaveTypeId || leave.id];
                                                        const isAnnuallyExpanded = isExpanded && isAnnually;

                                                        // Show Status toggle for:
                                                        // 1. Non-custom accrual settings, OR
                                                        // 2. Tiered leaves (even if they have custom settings, admin should control active/inactive), OR
                                                        // 3. Annually (Front-loaded) with enableAccrual false (use default settings, show status), OR
                                                        // 4. When custom accrual settings are expanded and frequency is Annually
                                                        const isAnnuallyWithAccrualDisabled = (leave.accrualFrequency === 'Annually' || leave.customAccrualFrequency === 'Annually') &&
                                                          leave.enableAccrual === false;
                                                        return !leave.hasCustomAccrualSettings || isTiered || isAnnuallyWithAccrualDisabled || isAnnuallyExpanded;
                                                      })() && (
                                                          <div className="col-12 col-md-3">
                                                            <label className="form-label fw-semibold mb-2">
                                                              Status
                                                            </label>
                                                            <div className="d-flex flex-column align-items-start">
                                                              <div className="d-flex align-items-center mb-2">
                                                                <div className="form-check form-switch me-3 d-flex align-items-center">
                                                                  <input
                                                                    className="form-check-input user-form-status-checkbox"
                                                                    type="checkbox"
                                                                    id={`status-${leave.id}`}
                                                                    checked={
                                                                      values.userLeaveSettings[index]
                                                                        ?.active !== false
                                                                    }
                                                                    onChange={(e) => {
                                                                      const currentValue = values.userLeaveSettings[index]?.value;
                                                                      const leaveTypeId = values.userLeaveSettings[index]?.leaveTypeId || values.userLeaveSettings[index]?.id;
                                                                      const checked = e.target.checked;

                                                                      setFieldValue(
                                                                        `userLeaveSettings[${index}].active`,
                                                                        checked
                                                                      );

                                                                      // Sync active with enableAccrual when frequency is Annually
                                                                      const editingSettings = editingAccrualSettings[leaveTypeId];
                                                                      const selectedFrequency = editingSettings?.customAccrualFrequency;
                                                                      const currentFrequency = leave.accrualFrequency || leave.customAccrualFrequency;
                                                                      const effectiveFrequency = selectedFrequency !== undefined ? selectedFrequency : currentFrequency;
                                                                      const isAnnually = effectiveFrequency === 'Annually';

                                                                      if (isAnnually) {
                                                                        // Update enableAccrual to match active status
                                                                        setFieldValue(
                                                                          `userLeaveSettings[${index}].enableAccrual`,
                                                                          checked
                                                                        );
                                                                        // Also update editingAccrualSettings if it exists
                                                                        if (editingSettings) {
                                                                          setEditingAccrualSettings({
                                                                            ...editingAccrualSettings,
                                                                            [leaveTypeId]: {
                                                                              ...editingAccrualSettings[leaveTypeId],
                                                                              enableAccrual: checked
                                                                            }
                                                                          });
                                                                        }
                                                                      }

                                                                      // When deactivating, save the current value but don't clear it
                                                                      // The value should be preserved even when inactive
                                                                      if (!checked) {
                                                                        // Save current value to restore if needed
                                                                        if (currentValue !== undefined && currentValue !== null) {
                                                                          originalValuesRef.current[leaveTypeId] = currentValue;
                                                                        }
                                                                        // Don't set value to 0 - preserve it for backend
                                                                      } else {
                                                                        // If reactivating, restore the original value if current value is 0
                                                                        const originalValue = originalValuesRef.current[leaveTypeId];
                                                                        if ((currentValue === 0 || currentValue === undefined || currentValue === null) &&
                                                                          originalValue !== undefined && originalValue !== null) {
                                                                          setFieldValue(
                                                                            `userLeaveSettings[${index}].value`,
                                                                            originalValue
                                                                          );
                                                                        }
                                                                      }
                                                                    }}
                                                                    style={{
                                                                      width: "3rem",
                                                                      height: "1.5rem",
                                                                    }}
                                                                  />
                                                                  <label
                                                                    className="form-check-label fw-semibold ms-2 mt-1"
                                                                    htmlFor={`status-${leave.id}`}
                                                                  >
                                                                    {values.userLeaveSettings[index]
                                                                      ?.active !== false
                                                                      ? "Active"
                                                                      : "Inactive"}
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <span
                                                                className={`badge px-3 py-2 ${values.userLeaveSettings[index]
                                                                  ?.active !== false
                                                                  ? "bg-success-subtle text-success"
                                                                  : "bg-danger-subtle text-danger"
                                                                  }`}
                                                              >
                                                                <i
                                                                  className={`bi me-1 ${values.userLeaveSettings[index]
                                                                    ?.active !== false
                                                                    ? "bi-check-circle-fill text-success"
                                                                    : "bi-x-circle-fill text-danger"
                                                                    }`}
                                                                ></i>
                                                                {values.userLeaveSettings[index]
                                                                  ?.active !== false
                                                                  ? "Active"
                                                                  : "Inactive"}
                                                              </span>
                                                              <small className="text-muted mt-2">
                                                                {values.userLeaveSettings[index]
                                                                  ?.active !== false
                                                                  ? "Leave type is enabled"
                                                                  : "Leave type is disabled"}
                                                              </small>
                                                            </div>
                                                          </div>
                                                        )}
                                                    </div>
                                                  ) : null;
                                                })()}

                                                {/* Enable Accrual checkbox for Tiered leaves - show outside allowance fields section */}
                                                {(() => {
                                                  // Check if this is a Tiered leave
                                                  const isTiered = leave.accrualFrequency === 'Tiered' ||
                                                    leave.customAccrualFrequency === 'Tiered' ||
                                                    leave.accrualStartFrequency === 'Tiered';

                                                  if (!isTiered) return null;

                                                  return (
                                                    <div className="row g-3 mt-2">
                                                      <div className="col-12 col-md-6">
                                                        <label className="form-label fw-semibold mb-2">
                                                          Accrual Settings
                                                        </label>
                                                        <div className="form-check form-switch">
                                                          <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`enableAccrual-${leave.id}`}
                                                            checked={
                                                              values.userLeaveSettings[index]?.enableAccrual !== false
                                                            }
                                                            onChange={(e) => {
                                                              const leaveTypeId = leave.leaveTypeId || leave.id;
                                                              const checked = e.target.checked;

                                                              // Update the enableAccrual value in form state
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].enableAccrual`,
                                                                checked
                                                              );

                                                              // Also mark the field as touched for validation
                                                              setFieldTouched(
                                                                `userLeaveSettings[${index}].enableAccrual`,
                                                                true
                                                              );

                                                              // Also update in editingAccrualSettings if it exists (for consistency and proper saving)
                                                              if (editingAccrualSettings[leaveTypeId]) {
                                                                setEditingAccrualSettings({
                                                                  ...editingAccrualSettings,
                                                                  [leaveTypeId]: {
                                                                    ...editingAccrualSettings[leaveTypeId],
                                                                    enableAccrual: checked
                                                                  }
                                                                });
                                                              }
                                                            }}
                                                            style={{
                                                              width: "3rem",
                                                              height: "1.5rem",
                                                            }}
                                                          />
                                                          <label
                                                            className="form-check-label fw-semibold ms-2 mt-1"
                                                            htmlFor={`enableAccrual-${leave.id}`}
                                                          >
                                                            Enable Accrual
                                                          </label>
                                                        </div>
                                                        <small className="text-muted d-block mt-2">
                                                          Enable or disable accrual for this user's Tiered leave
                                                        </small>
                                                      </div>
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}

                                      {/* Non-Deductible Allowances Section */}
                                      {
                                        nonDeductibleLeaves.length > 0 && (
                                          <div className="mb-4">
                                            <div className="non-deductible-allowances-header">
                                              <div className="non-deductible-allowances-header-content">
                                                <i className="bi bi-calendar-check-fill non-deductible-allowances-icon"></i>
                                                <h5 className="non-deductible-allowances-title">Non-Deductible Allowances</h5>
                                              </div>
                                            </div>
                                            <div className="row g-3">
                                              {nonDeductibleLeaves.map((leave) => {
                                                const index = values.userLeaveSettings.findIndex(
                                                  (l) => l.id === leave.id || l.leaveTypeId === leave.leaveTypeId
                                                );
                                                return (
                                                  <div
                                                    key={leave.id}
                                                    className="col-12 col-md-6 col-lg-4"
                                                  >
                                                    <div
                                                      className="p-3 border rounded h-100"
                                                      style={{
                                                        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                                                        borderColor: "#e9ecef !important",
                                                        transition: "all 0.3s ease",
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                                                        e.currentTarget.style.transform = "translateY(-2px)";
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        e.currentTarget.style.boxShadow = "none";
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                      }}
                                                    >
                                                      {/* Header with Leave Name and Status Toggle */}
                                                      <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div className="flex-grow-1">
                                                          <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                                                            {leave.name}
                                                          </h6>
                                                          <small className="text-muted d-block">Non-Deductible Leave</small>
                                                          {/* Accrual Information Badge */}
                                                          {leave.accrualMethod && leave.accrualMethod !== 'None' && (
                                                            <div className="mt-1">
                                                              <span className="badge bg-info-subtle text-info me-2" style={{ fontSize: "0.7rem" }}>
                                                                <i className="bi bi-arrow-repeat me-1"></i>
                                                                Accrual: {leave.accrualMethod}
                                                              </span>
                                                              {leave.accrualMethod === 'Tiered' && leave.tieredRates && leave.tieredRates.length > 0 && values.empStartDate && (
                                                                <span className="badge bg-warning-subtle text-warning" style={{ fontSize: "0.7rem" }}>
                                                                  <i className="bi bi-info-circle me-1"></i>
                                                                  {(() => {
                                                                    const empStartDate = new Date(values.empStartDate);
                                                                    const today = new Date();
                                                                    const yearsOfService = (today - empStartDate) / (1000 * 60 * 60 * 24 * 365.25);
                                                                    const applicableTier = leave.tieredRates
                                                                      .sort((a, b) => (b.years || 0) - (a.years || 0))
                                                                      .find(tier => yearsOfService >= (tier.years || 0)) || leave.tieredRates[0];
                                                                    return `${applicableTier.days || 0} days/year`;
                                                                  })()}
                                                                </span>
                                                              )}
                                                              {leave.accrualMethod !== 'Tiered' && leave.accrualMethod !== 'Front-loaded' && leave.accrualRate > 0 && (
                                                                <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: "0.7rem" }}>
                                                                  {leave.accrualRate} days/{leave.accrualMethod === 'Monthly' ? 'month' : leave.accrualMethod === 'Bi-weekly' ? 'bi-week' : leave.accrualMethod === 'Weekly' ? 'week' : 'period'}
                                                                </span>
                                                              )}
                                                              {leave.accruedBalance !== undefined && leave.accruedBalance > 0 && (
                                                                <span className="badge bg-success-subtle text-success ms-3" style={{ fontSize: "0.7rem" }}>
                                                                  <i className="bi bi-check-circle me-1"></i>
                                                                  Accrued: {leave.accruedBalance.toFixed(2)} days
                                                                </span>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                        <div className="form-check form-switch">
                                                          <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`status-${leave.id}`}
                                                            checked={
                                                              values.userLeaveSettings[index]
                                                                ?.active !== false
                                                            }
                                                            onChange={(e) => {
                                                              const currentValue = values.userLeaveSettings[index]?.value;
                                                              const leaveTypeId = values.userLeaveSettings[index]?.leaveTypeId || values.userLeaveSettings[index]?.id;

                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].active`,
                                                                e.target.checked
                                                              );

                                                              if (!e.target.checked) {
                                                                if (currentValue !== undefined && currentValue !== null) {
                                                                  originalValuesRef.current[leaveTypeId] = currentValue;
                                                                }
                                                              } else {
                                                                const originalValue = originalValuesRef.current[leaveTypeId];
                                                                if ((currentValue === 0 || currentValue === undefined || currentValue === null) &&
                                                                  originalValue !== undefined && originalValue !== null) {
                                                                  setFieldValue(
                                                                    `userLeaveSettings[${index}].value`,
                                                                    originalValue
                                                                  );
                                                                }
                                                              }
                                                            }}
                                                            style={{
                                                              width: "2.5rem",
                                                              height: "1.25rem",
                                                              cursor: "pointer"
                                                            }}
                                                          />
                                                        </div>
                                                      </div>

                                                      {/* Type Selector */}
                                                      <div className="mb-3">
                                                        <label className="form-label fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>
                                                          Type
                                                        </label>
                                                        <div className="btn-group w-100" role="group">
                                                          <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name={`userLeaveSettings[${index}].type`}
                                                            id={`days-${leave.id}`}
                                                            value="Days"
                                                            checked={
                                                              values.userLeaveSettings[index]
                                                                .type === "Days"
                                                            }
                                                            onChange={() => {
                                                              const currentValue = values.userLeaveSettings[index]?.value || 0;
                                                              const currentType = values.userLeaveSettings[index]?.type || "Days";
                                                              // Convert from Hours to Days: divide by 8
                                                              const convertedValue = currentType === "Hours" ? currentValue / 8 : currentValue;
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].type`,
                                                                "Days"
                                                              );
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].value`,
                                                                convertedValue
                                                              );
                                                            }}
                                                          />
                                                          <label
                                                            className={`btn ${values.userLeaveSettings[index].type === "Days" ? "btn-primary" : "btn-outline-primary"}`}
                                                            htmlFor={`days-${leave.id}`}
                                                            style={{ fontSize: "0.85rem", padding: "0.375rem 0.75rem" }}
                                                          >
                                                            Days
                                                          </label>
                                                          <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name={`userLeaveSettings[${index}].type`}
                                                            id={`hours-${leave.id}`}
                                                            value="Hours"
                                                            checked={
                                                              values.userLeaveSettings[index]
                                                                .type === "Hours"
                                                            }
                                                            onChange={() => {
                                                              const currentValue = values.userLeaveSettings[index]?.value || 0;
                                                              const currentType = values.userLeaveSettings[index]?.type || "Days";
                                                              // Convert from Days to Hours: multiply by 8
                                                              const convertedValue = currentType === "Days" ? currentValue * 8 : currentValue;
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].type`,
                                                                "Hours"
                                                              );
                                                              setFieldValue(
                                                                `userLeaveSettings[${index}].value`,
                                                                convertedValue
                                                              );
                                                            }}
                                                          />
                                                          <label
                                                            className={`btn ${values.userLeaveSettings[index].type === "Hours" ? "btn-primary" : "btn-outline-primary"}`}
                                                            htmlFor={`hours-${leave.id}`}
                                                            style={{ fontSize: "0.85rem", padding: "0.375rem 0.75rem" }}
                                                          >
                                                            Hours
                                                          </label>
                                                        </div>
                                                      </div>

                                                      {/* Utilized Display */}
                                                      <div className="mb-3">
                                                        <label className="form-label fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>
                                                          Utilized
                                                        </label>
                                                        <div
                                                          className="d-flex align-items-center p-2 rounded"
                                                          style={{
                                                            backgroundColor: "#f8f9fa",
                                                            border: "1px solid #dee2e6"
                                                          }}
                                                        >
                                                          <i className="bi bi-clock-history text-muted me-2"></i>
                                                          <span className="fw-bold" style={{ fontSize: "1.1rem" }}>
                                                            {values.userLeaveSettings[index]?.usedLeaves || 0}
                                                          </span>
                                                          <span className="text-muted ms-2" style={{ fontSize: "0.85rem" }}>
                                                            {values.userLeaveSettings[index].type}
                                                          </span>
                                                        </div>
                                                        <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                          Previously used/disabled balances
                                                        </small>
                                                      </div>

                                                      {/* Status Badge */}
                                                      <div className="d-flex justify-content-center">
                                                        <span
                                                          className={`badge px-3 py-2 ${values.userLeaveSettings[index]
                                                            ?.active !== false
                                                            ? "bg-success-subtle text-success border border-success"
                                                            : "bg-danger-subtle text-danger border border-danger"
                                                            }`}
                                                          style={{ fontSize: "0.85rem" }}
                                                        >
                                                          <i
                                                            className={`bi me-1 ${values.userLeaveSettings[index]
                                                              ?.active !== false
                                                              ? "bi-check-circle-fill"
                                                              : "bi-x-circle-fill"
                                                              }`}
                                                          ></i>
                                                          {values.userLeaveSettings[index]
                                                            ?.active !== false
                                                            ? "Active"
                                                            : "Inactive"}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )
                                      }
                                    </>
                                  );
                                })()
                              ) : (
                                <div className="text-center py-4">
                                  <i
                                    className="bi bi-calendar-x text-muted"
                                    style={{ fontSize: "3rem" }}
                                  ></i>
                                  <p className="text-muted mt-3 mb-0">
                                    No leave types available
                                  </p>
                                  <small className="text-muted">
                                    Please ensure leave types are configured in
                                    the system
                                  </small>
                                </div>
                              )}
                            </div >
                          </div>

                          {/* Additional Options */}
                          <div
                            style={{ transform: "none" }}
                            className="card mb-3 border-0 shadow-sm w-100"
                          >
                            <div className="card-header user-form-card-header">
                              <div className="px-5">
                                <h5 className="mb-0 fw-bold">
                                  <i className="bi bi-gear-fill me-2 text-white"></i>
                                  Additional Options
                                </h5>
                                <p className="mb-0 mt-2 text-light small">
                                  Additional settings and preferences
                                </p>
                              </div>
                            </div>
                            <div className="card-body p-4">
                              {mode !== "edit" && (
                                <div className="form-check mb-3">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sendLoginDetails"
                                    name="isFowardLoginDetails"
                                    checked={values.isFowardLoginDetails}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "isFowardLoginDetails",
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label fw-semibold"
                                    htmlFor="sendLoginDetails"
                                  >
                                    Send login details to user email
                                  </label>
                                  <small className="form-text text-muted d-block mt-2">
                                    User will receive an email with their login
                                    credentials. After logging in, they can <b>reset</b> their password if needed.
                                  </small>
                                </div>
                              )}
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="userStatus"
                                  name="isLoginDisabled"
                                  checked={!values.isLoginDisabled}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "isLoginDisabled",
                                      !e.target.checked
                                    )
                                  }
                                />
                                <label
                                  className="form-check-label fw-semibold"
                                  htmlFor="userStatus"
                                >
                                  Enable user login access
                                </label>
                                <small className="form-text text-muted d-block mt-2">
                                  When unchecked, the user will not be able to log in to the system. This option is useful when admin does not want the user to have login access and prefers to handle account management directly.
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Delete User - Only in Edit Mode and if user is not admin */}
                          {mode === "edit" && values.role !== 1 && values.role !== 2 && (
                            <div
                              style={{ transform: "none" }}
                              className="card mb-3 border-0 shadow-sm w-100"
                            >
                              <div className="card-header user-form-status-card-header">
                                <div className="px-5">
                                  <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-trash-fill me-2 text-danger"></i>
                                    Delete User Permanently
                                  </h5>
                                  <p className="mb-0 mt-2 text-dark small">
                                    Warning: Permanent Deletion
                                  </p>
                                </div>
                              </div>
                              <div className="card-body p-4">
                                <div className="row align-items-center">
                                  <div className="col-md-4">
                                    <button
                                      type="button"
                                      className="user-form-btn user-form-btn-danger"
                                      onClick={() => setShowDeleteConfirmModal(true)}
                                      disabled={userStore.isLoading || userStore.userDataLoading}
                                    >
                                      {(userStore.isLoading || userStore.userDataLoading) ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                          ></span>
                                          Deleting...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-trash-fill" style={{ color: "#fff", display: "inline-flex", alignItems: "center", lineHeight: 1 }}></i>
                                          <span style={{ color: "#fff" }}>Delete User Permanently</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                                <div className="alert alert-danger mt-3 mb-0">
                                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                  <strong>Warning:</strong>{" "}
                                  Permanently deleting this user will remove all their data from the system, including profile information, leave records, attendance data, and all associated records. This action cannot be recovered.
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="user-form-actions">
                            <button
                              type="button"
                              className="user-form-btn user-form-btn-cancel"
                              onClick={() => props.history.goBack()}
                            >
                              <i className="bi bi-arrow-left"></i>
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={mode === "create" && isSuspended || userStore.isLoading || userStore.userDataLoading || leaveTypesLoading}
                              className="user-form-btn user-form-btn-submit"
                            >
                              {(userStore.isLoading || userStore.userDataLoading) ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                  ></span>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i
                                    className={`bi ${mode === "edit"
                                      ? "bi-check-circle"
                                      : "bi-person-plus"
                                      }`}
                                  ></i>
                                  {mode === "edit"
                                    ? "Update User"
                                    : "Create User"}
                                </>
                              )}
                            </button>
                          </div>
                        </form>

                        {/* Delete User Permanently Confirmation Modal */}
                        {showDeleteConfirmModal && (
                          <div
                            className="modal fade show"
                            style={{
                              display: "flex",
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              zIndex: 1050,
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1rem"
                            }}
                            onClick={() => !userStore.isLoading && setShowDeleteConfirmModal(false)}
                          >
                            <div
                              className="modal-dialog"
                              style={{
                                margin: 0,
                                maxWidth: "500px",
                                width: "100%"
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                className="modal-content delete-confirm-modal-content"
                                style={{
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                                  border: "none"
                                }}
                              >
                                <div
                                  className="modal-header text-white d-flex align-items-center"
                                  style={{
                                    padding: "16px 20px"
                                  }}
                                >
                                  <h5 className="modal-title mb-0 d-flex align-items-center">
                                    <i className="bi bi-trash-fill me-2" style={{ color: "#fff" }}></i>
                                    Delete User Permanently?
                                  </h5>
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => !userStore.isLoading && setShowDeleteConfirmModal(false)}
                                    aria-label="Close"
                                  />
                                </div>
                                <div className="modal-body" style={{ padding: "20px" }}>
                                  <p className="mb-3" style={{ lineHeight: "1.6", fontSize: "0.9375rem" }}>
                                    Are you sure you want to permanently delete{" "}
                                    <strong>"{getUserInfo.name || userStore.users?.name}"</strong>?
                                  </p>
                                  <p className="text-muted small mb-2">This action cannot be undone. All user data will be permanently removed, including:</p>
                                  <ul className="mb-0 ps-3" style={{ fontSize: "0.9375rem", lineHeight: "1.7" }}>
                                    <li>User profile and account information</li>
                                    <li>Leave records and balances</li>
                                    <li>Attendance and time tracking data</li>
                                    <li>All associated employee records</li>
                                  </ul>
                                  <p className="mt-3 mb-0 small text-muted">Click Delete to proceed with permanent deletion.</p>
                                </div>
                                <div className="modal-footer border-top bg-light" style={{ padding: "16px 20px", gap: "10px" }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ borderRadius: "8px" }}
                                    onClick={() => setShowDeleteConfirmModal(false)}
                                    disabled={userStore.isLoading}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn text-white user-form-delete-confirm-btn d-inline-flex align-items-center"
                                    style={{
                                      background: "#EE6352",
                                      borderRadius: "8px",
                                      border: "none",
                                      gap: "8px"
                                    }}
                                    disabled={userStore.isLoading}
                                    onClick={async () => {
                                      if (!userId) return;
                                      const result = await dispatch(onDeleteUser(userId, null));
                                      if (result?.success) {
                                        setShowDeleteConfirmModal(false);
                                        props.history.push("/users");
                                      }
                                    }}
                                  >
                                    {userStore.isLoading ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Deleting...
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-trash-fill" style={{ color: "#fff", display: "inline-flex", alignItems: "center", lineHeight: 1, flexShrink: 0 }}></i>
                                        Delete
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Leave Year Start Info Modal */}
                        {showLeaveYearInfoPopup && proratedInfo && (
                          <div
                            className="modal fade show"
                            style={{
                              display: "flex",
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              zIndex: 1050,
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1rem"
                            }}
                            onClick={() => setShowLeaveYearInfoPopup(false)}
                          >
                            <div
                              className="modal-dialog"
                              style={{
                                margin: 0,
                                maxWidth: "500px",
                                width: "100%"
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="modal-content">
                                <div className="modal-header bg-info text-white">
                                  <h5 className="modal-title">
                                    <i className="bi bi-info-circle-fill me-2"></i>
                                    Proportional Leave Calculation
                                  </h5>
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowLeaveYearInfoPopup(false)}
                                  ></button>
                                </div>
                                <div className="modal-body">
                                  <div style={{ fontSize: "0.9375rem" }}>
                                    <p className="mb-3" style={{ lineHeight: "1.6" }}>
                                      Since the leave year starts in <strong className="text-info">{proratedInfo.selectedMonth}</strong>,
                                      leave allowances will be calculated proportionally based on the remaining months in the current leave year.
                                    </p>
                                    <div className="bg-light p-3 rounded mb-3">
                                      <div className="row g-3">
                                        <div className="col-6">
                                          <div className="text-muted small mb-1">Leave Year Start Month</div>
                                          <strong className="text-dark d-block" style={{ fontSize: "1rem" }}>
                                            {proratedInfo.selectedMonth}
                                          </strong>
                                        </div>
                                        <div className="col-6">
                                          <div className="text-muted small mb-1">Remaining Months</div>
                                          <strong className="text-info d-block" style={{ fontSize: "1rem" }}>
                                            {proratedInfo.monthsRemaining} months
                                          </strong>
                                        </div>
                                        <div className="col-12 pt-2 border-top">
                                          <div className="text-muted small mb-1">Proration Factor</div>
                                          <strong className="text-info d-block" style={{ fontSize: "1.25rem" }}>
                                            {(proratedInfo.prorationFactor * 100).toFixed(1)}%
                                          </strong>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="alert alert-info border-start border-info border-3 mb-0">
                                      <strong>Example Calculation:</strong>
                                      <ul className="mb-0 mt-2" style={{ paddingLeft: "1.5rem" }}>
                                        <li>If you assign <strong>12 days</strong> of annual leave</li>
                                        <li>User will receive <strong>{(12 * proratedInfo.prorationFactor).toFixed(1)} days</strong> for the current year</li>
                                        <li>Starting from the next leave year cycle, they will receive the full <strong>12 days</strong> allocation</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="modal-footer">
                                  <button
                                    type="button"
                                    className="btn btn-info"
                                    onClick={() => setShowLeaveYearInfoPopup(false)}
                                  >
                                    Got it
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Deductible Allowances Info Modal */}
                        {showDeductibleInfoPopup && proratedInfo && (
                          <div
                            className="modal fade show"
                            style={{
                              display: "flex",
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              zIndex: 1050,
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1rem"
                            }}
                            onClick={() => setShowDeductibleInfoPopup(false)}
                          >
                            <div
                              className="modal-dialog modal-lg"
                              style={{
                                margin: 0,
                                maxWidth: "800px",
                                width: "100%"
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="modal-content">
                                <div className="modal-header bg-warning text-dark">
                                  <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    Proportional Leave Allocation Notice
                                  </h5>
                                  <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDeductibleInfoPopup(false)}
                                  ></button>
                                </div>
                                <div className="modal-body">
                                  <div style={{ fontSize: "0.9375rem" }}>
                                    <p className="mb-3" style={{ lineHeight: "1.6" }}>
                                      Based on the selected leave year start month (<strong className="text-warning">{proratedInfo.selectedMonth}</strong>),
                                      the leave allowances you assign below will be automatically prorated for the current leave year.
                                    </p>
                                    <div className="bg-light p-3 rounded mb-3">
                                      <div className="row g-3">
                                        <div className="col-md-4">
                                          <div className="text-muted small mb-1">Leave Year Start</div>
                                          <strong className="text-dark d-block" style={{ fontSize: "1rem" }}>
                                            {proratedInfo.selectedMonth}
                                          </strong>
                                        </div>
                                        <div className="col-md-4">
                                          <div className="text-muted small mb-1">Remaining Months</div>
                                          <strong className="text-dark d-block" style={{ fontSize: "1rem" }}>
                                            {proratedInfo.monthsRemaining} months
                                          </strong>
                                        </div>
                                        <div className="col-md-4">
                                          <div className="text-muted small mb-1">Proration Factor</div>
                                          <strong className="text-warning d-block" style={{ fontSize: "1.25rem" }}>
                                            {(proratedInfo.prorationFactor * 100).toFixed(1)}%
                                          </strong>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="alert alert-warning border-start border-warning border-3 mb-0">
                                      <strong>How It Works:</strong>
                                      <ul className="mb-0 mt-2" style={{ paddingLeft: "1.5rem" }}>
                                        <li>If you assign <strong>12 days</strong> of annual leave</li>
                                        <li>The user will receive <strong>{(12 * proratedInfo.prorationFactor).toFixed(1)} days</strong> for the current year</li>
                                        <li>This is prorated based on <strong>{proratedInfo.monthsRemaining} remaining months</strong> in the leave year</li>
                                        <li>Starting from the next leave year cycle, they will receive the full <strong>12 days</strong> allocation</li>
                                      </ul>
                                      <p className="mb-0 mt-3 pt-2 border-top">
                                        <small className="text-muted">
                                          <i className="bi bi-lightbulb-fill me-1"></i>
                                          <strong>Note:</strong> This proration only applies to the current leave year.
                                          All subsequent leave years will use the full allocated amount.
                                        </small>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="modal-footer">
                                  <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => setShowDeductibleInfoPopup(false)}
                                  >
                                    Understood
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Leave & timesheet approver (manager) info */}
                        {showApproverManagerInfoPopup && (
                          <div
                            className="modal fade show"
                            style={{
                              display: "flex",
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              zIndex: 1050,
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "1rem",
                            }}
                            onClick={() => setShowApproverManagerInfoPopup(false)}
                          >
                            <div
                              className="modal-dialog"
                              style={{
                                margin: 0,
                                maxWidth: "560px",
                                width: "100%",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="modal-content">
                                <div className="modal-header bg-info text-white">
                                  <h5 className="modal-title">
                                    <i className="bi bi-info-circle-fill me-2"></i>
                                    {t("app.users.approverManagerInfoTitle")}
                                  </h5>
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowApproverManagerInfoPopup(false)}
                                  ></button>
                                </div>
                                <div className="modal-body">
                                  <div style={{ fontSize: "0.9375rem", lineHeight: 1.6 }}>
                                    <p className="mb-3">{t("app.users.approverManagerInfoLead")}</p>
                                    <p className="mb-0">{t("app.users.approverManagerInfoProjects")}</p>
                                  </div>
                                </div>
                                <div className="modal-footer">
                                  <button
                                    type="button"
                                    className="btn btn-info"
                                    onClick={() => setShowApproverManagerInfoPopup(false)}
                                  >
                                    {t("app.common.close")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </Dimmer.Dimmable>
      </div>
    </Container>
  );
};

export default UserForm;
